// These calls bypass supabase-js on purpose (binary downloads / streaming);
// the timeout policy must not bypass with them.
import { fetchWithTimeout } from "@/lib/supabaseFetch";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { cn, compareSetTimes } from "@/lib/utils";
import { DEFAULT_TIMEZONE, TIMEZONES } from "@/lib/timezones";
import { computeAssignmentTimeUpdates } from "@/lib/syncAssignmentTimes";
import { reconstructFestivalDays } from "@/lib/doorsForDay";
import { saveDayDoors } from "@/lib/dayDoorsSave";
import { briefingDayDoorsQuery } from "@/lib/queries/productionQueries";
import { buildStageRenameMap } from "@/lib/saveFlow/stageRenameMap";
import {
  type SaveScope,
  type ScopeStateSnapshot,
  stepScope,
  computeScopeFlags,
  isScopeDirty,
  captureSavedSnapshot,
} from "@/lib/saveScope";
import { productionOverviewPath, resolveWarningContinue } from "@/lib/productionNavigation";
import { applyAssignmentTimePatches } from "@/lib/applyAssignmentPatches";
import { legacyCrewToRecords } from "@/lib/migrations/legacyCrewToRecords";
import { partitionCrewSave } from "@/lib/saveFlow/partitionCrewSave";
import { applyPersonFieldAcrossDays } from "@/lib/saveFlow/applyPersonFieldAcrossDays";
import {
  ScheduleChangedElsewhereError,
  checkScheduleRevision,
  createScheduleRevisionTracker,
  finishScheduleSave,
  readWithScheduleRevision,
  scheduleSaveBody,
  scheduleWriteAtRevision,
  shouldAdoptScheduleRead,
} from "@/lib/saveFlow/scheduleRevision";
import { toast as sonnerToast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { uploadFestivalMap } from "@/lib/festivalMap";
import { validateFestivalMapFile } from "@/lib/festivalMapValidation";
import { FestivalMapPreview } from "@/components/FestivalMapPreview";
import { WeatherCard } from "@/components/WeatherCard";
import { VideoExamplesEditor } from "@/components/briefing/VideoExamplesEditor";
import { replaceLineup, type LineupRow } from "@/lib/lineupSave";
import { invalidateBriefingCaches } from "@/lib/invalidateBriefingCaches";
import {
  parseLocationFromStageName,
  isCameraIdRole,
  findDuplicateCameraIds,
  CREW_ROLES,
  formatDayLabel,
  parseCustomFields,
  parseVideoExamples,
  filledVideoExamples,
  visibleCustomFields,
  cameraSettingsText,
  PRODUCTION_STEPS,
  type BriefingCustomField,
  type BriefingVideoExample,
  PRO_PRODUCTIONS_PER_YEAR,
} from "@produce/shared";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Upload,
  Loader2,
  Trash2,
  AlertTriangle,
  Mail,
  Save,
  Download,
  CheckCircle2,
  Clock,
  Pencil,
  Users,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AppSidebar from "@/components/AppSidebar";
import { WizardStepper } from "@/components/wizard/WizardStepper";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { CameraIdCard, type CameraIdRow } from "@/components/briefing/CameraIdCard";
import { FlightsSection } from "@/components/briefing/FlightsSection";
import { ReviewPublishStep } from "@/components/briefing/ReviewPublishStep";
import { LineupImportTab } from "@/components/LineupImportTab";
import { CrewSchedule } from "@/components/CrewSchedule";
import { LiveTrackingSetup } from "@/components/LiveTrackingSetup";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import { AddToTeamButton } from "@/components/briefing/AddToTeamButton";
import { useProductionState } from "@/hooks/useProductionState";
import { useProducerSettings } from "@/hooks/useProducerSettings";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { format } from "date-fns";
import { DateChangeWarningDialog } from "@/components/DateChangeWarningDialog";
import OnboardingGuide, { type GuideStep } from "@/components/OnboardingGuide";
import type {
  CateringSettings,
  Restrictions,
  ValidationResult,
  AssignmentFormData,
} from "@/types/domain";
import type { Json, Tables, TablesInsert } from "@/integrations/supabase/types";
import { parseStages, parseCustomSubjects, parseGeoFeature } from "@/types/domain";
import { buildStageIdResolver, wizardStagesForDay } from "@/lib/briefingStages";
import { invokeErrorMessage } from "@/lib/invokeErrorMessage";
import { safeLocalStorage } from "@/lib/safeStorage";

// Postgres time columns occasionally return HH:MM:SS while form
// state holds HH:MM. Normalise to HH:MM before comparison so a
// reload doesn't misclassify an unchanged assignment as edited.
const normalizeTime = (t: string | null | undefined): string => (t ? t.substring(0, 5) : "");

interface Stage {
  name: string;
  label: string;
}
interface CustomSubject {
  name: string;
  label: string;
  location?: string;
}
interface TimetableEntry {
  id?: string;
  stage_name: string;
  event_date: string;
  artist_name: string;
  set_start: string;
  set_end: string;
  // Producer-marked "important" artist — surfaced with a glow in the
  // schedule creator (CrewSchedule). Round-trips via the lineup table.
  highlighted?: boolean;
}
interface CrewMember {
  id?: string;
  // Stable client-side identity per person — assigned at row creation
  // (addPersonAcrossDays) or backfilled on load (one shared uuid per
  // unique lower(email)). Survives email edits so the editor's group
  // key never changes mid-keystroke. Stripped from save payloads.
  _personId?: string;
  name: string;
  email: string;
  phone: string;
  function: string;
  arrival_time: string;
  event_date: string;
  // Camera ID (1–3 letters) for this person on this day. Per-(person,
  // day) like arrival_time/function, so it lives on the crew row, not
  // the person. Only meaningful for camera/photo functions.
  camera_id?: string;
  color?: string;
  hasColor?: boolean;
  isExistingUser?: boolean;
}

// CREW_ROLES now lives in @produce/shared (single source of truth,
// shared with My Team). Imported above.

// briefings.schedule_revision, read right before a schedule slice so the slice
// carries the revision it was read at (lib/saveFlow/scheduleRevision.ts).
// null when it cannot be read (e.g. this bundle is live before the migration
// that adds the column): the schedule still loads and saves unchecked, as
// before, instead of the editor showing — and then saving — an empty schedule.
const readScheduleRevision = async (briefingId: string): Promise<number | null> => {
  const { data, error } = await supabase
    .from("briefings")
    .select("schedule_revision")
    .eq("id", briefingId)
    .single();
  return error ? null : data.schedule_revision;
};

// Stable per-person key for grouping a person's day rows when editing
// Camera IDs: prefer the client-side _personId, fall back to the
// normalized email. Mirrors the crew identity rule used elsewhere.
const cameraIdPersonKey = (m: { _personId?: string; email: string }): string =>
  m._personId || (m.email || "").trim().toLowerCase();

// Returns the first event_date on which two crew members share a Camera
// ID (case-insensitive), or null when all days are conflict-free. Camera
// IDs are unique PER DAY; there is no hard DB constraint, so the save
// flow enforces this.
function firstCameraIdConflictDate(
  crew: Array<{
    function: string;
    _personId?: string;
    email: string;
    event_date: string;
    camera_id?: string;
  }>,
): string | null {
  const byDay = new Map<string, { key: string; cameraId: string }[]>();
  for (const m of crew) {
    if (!isCameraIdRole(m.function)) continue;
    const personKey = cameraIdPersonKey(m);
    if (!personKey) continue;
    const list = byDay.get(m.event_date) ?? [];
    if (!list.some((e) => e.key === personKey)) {
      list.push({ key: personKey, cameraId: m.camera_id || "" });
      byDay.set(m.event_date, list);
    }
  }
  for (const [day, entries] of byDay) {
    if (findDuplicateCameraIds(entries).size > 0) return day;
  }
  return null;
}

const DEFAULT_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#FFA07A",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E2",
  "#F8B739",
  "#52B788",
  "#FF9FF3",
  "#54A0FF",
  "#FF6348",
  "#2ECC71",
  "#3498DB",
];
// The step labels live in @produce/shared: the admin panel turns a stored
// `last_edit_step` back into a step name, and a second copy of this list would
// eventually name a different screen than the one the producer is on.
const STEPS = PRODUCTION_STEPS;

// Per-step save scoping lives in @/lib/saveScope so it can be unit
// tested without booting the whole editor. See that module for the
// step → scope mapping rationale.
const CreateEditBriefing = () => {
  const { id: urlBriefingId, productionId: urlProductionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { subscriptionStatus, user, userId, session } = useAuth();
  const { expanded } = useSidebar();
  // Honours the producer's 12h/24h setting; handed down to the steps that
  // render times, so they do not each reach for settings of their own.
  const formatTime = useProducerTimeFormatter();

  // Single source of truth for briefing ID - use URL ID when editing (either from /briefings/:id or /productions/:productionId)
  const currentBriefingId = urlBriefingId || urlProductionId;

  // Check if we should start at a specific step (e.g., from query param)
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const initialStep = searchParams.get("step") ? parseInt(searchParams.get("step")!) : 0;
  const isShortcut = searchParams.get("shortcut") === "true";
  const [currentStep, setCurrentStep] = useState(initialStep);
  // Latches `true` for the entire duration of a step navigation —
  // covers the window between handleSave resolving (which flips
  // useProductionState's isSaving back to false) and setCurrentStep
  // re-rendering the next step. Without this the Next button briefly
  // re-enables and a fast double-click double-saved.
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showBriefingGuide, setShowBriefingGuide] = useState(false);
  const [briefingGuideShownForStep, setBriefingGuideShownForStep] = useState<Set<number>>(
    new Set(),
  );

  // Production state management with auto-save
  const { productionId, isSaving, saveProduction, clearProductionState } =
    useProductionState(currentBriefingId);

  // Producer settings for default assignment length
  const { settings: producerSettings } = useProducerSettings();

  // Basic Information
  const [festivalName, setFestivalName] = useState("");
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [festivalDays, setFestivalDays] = useState<
    {
      date: string;
      startTime: string;
      endTime: string;
      // Optional producer-chosen name shown after the "Day N" prefix
      // (e.g. "Harmony of Hardcore"). Falls back to the date when blank.
      name?: string;
    }[]
  >([
    {
      date: new Date().toISOString().split("T")[0],
      startTime: "12:00",
      endTime: "23:00",
    },
  ]);
  // Which day's name field is open for inline editing in step 0 (null = none).
  const [editingDayNameIndex, setEditingDayNameIndex] = useState<number | null>(null);
  // Selected day on the multi-day review/publish step (step 8).
  const [reviewDayIndex, setReviewDayIndex] = useState(0);
  // Collapse the left step nav to reclaim width (e.g. on the crew
  // schedule step). Toggled by a chevron button next to it.
  const [stepperCollapsed, setStepperCollapsed] = useState(false);
  const [stages, setStages] = useState<Stage[]>([]);
  // Original stage labels from DB — used to detect renames at save time
  const [originalStageLabels, setOriginalStageLabels] = useState<Map<string, string>>(new Map());
  const [customSubjects, setCustomSubjects] = useState<CustomSubject[]>([]);
  // Producer-defined extra briefing cards (e.g. "Flights" + details).
  const [customFields, setCustomFields] = useState<BriefingCustomField[]>([]);
  // The non-empty subset, shown on the review step + persisted on save.
  const visibleEditorCustomFields = useMemo(
    () => visibleCustomFields(customFields),
    [customFields],
  );
  // Reference videos for the crew (YouTube/Vimeo/Instagram/Facebook).
  const [videoExamples, setVideoExamples] = useState<BriefingVideoExample[]>([]);
  // Blank rows are not persisted; the review card shows supported links only.
  const filledEditorVideoExamples = useMemo(
    () => filledVideoExamples(videoExamples),
    [videoExamples],
  );

  // Pending lineup-arrow swap awaiting producer confirmation. We
  // compute the assignment impact BEFORE applying the swap so the
  // producer sees exactly what will change and accepts explicitly.
  // Bypasses the dialog when the swap touches no assignments.
  const [pendingMoveSwap, setPendingMoveSwap] = useState<{
    apply: () => void;
    fromArtist: string;
    toArtist: string;
    stageName: string;
    relabeled: number;
    timeMoved: number;
    deactivated: number;
  } | null>(null);

  // General Briefing
  const [festivalDateStart, setFestivalDateStart] = useState("");
  const [festivalDateEnd, setFestivalDateEnd] = useState("");
  const [navigationAddress, setNavigationAddress] = useState("");
  const [hotelAddress, setHotelAddress] = useState("");
  // Flights are edited in their own dialog and written straight to
  // briefing_flights; only this producer toggle rides along with the
  // rest of the general briefing save.
  const [flightsEnabled, setFlightsEnabled] = useState(false);
  const [arrivalTime, setArrivalTime] = useState("");
  const [clientName, setClientName] = useState("");
  const [crewDresscode, setCrewDresscode] = useState("");
  const [crewCheckin, setCrewCheckin] = useState("");
  const [cameraSettings, setCameraSettings] = useState("");
  const [audioSettings, setAudioSettings] = useState("");
  const [extraSettings, setExtraSettings] = useState("");
  const [restrictions, setRestrictions] = useState("");
  const [generalInstructions, setGeneralInstructions] = useState("");
  const [producerName, setProducerName] = useState("");
  const [producerEmail, setProducerEmail] = useState("");
  const [producerPhone, setProducerPhone] = useState("");
  const [invoiceInformation, setInvoiceInformation] = useState("");

  // Catering with enable/disable toggles
  const [lunchEnabled, setLunchEnabled] = useState(false);
  const [dinnerEnabled, setDinnerEnabled] = useState(false);
  const [nightsnackEnabled, setNightsnackEnabled] = useState(false);

  const [lunch, setLunch] = useState({
    start: "12:00",
    end: "14:00",
  });
  const [dinner, setDinner] = useState({
    start: "18:00",
    end: "20:00",
  });
  const [nightsnack, setNightsnack] = useState({
    start: "02:00",
    end: "04:00",
  });

  const [others, setOthers] = useState("");

  // Timetable with AI
  const [timetable, setTimetable] = useState<TimetableEntry[]>([]);

  // Crew
  const [crew, setCrew] = useState<CrewMember[]>([]);

  // Team member autocomplete
  const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState<number | null>(null);

  // Map. mapStoragePath points at briefing-maps storage; mapFile is a
  // pending local upload.
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [mapStoragePath, setMapStoragePath] = useState<string | null>(null);

  // Most recent post-apply snapshot of briefing_stages from the save
  // flow — used by the invalidateBriefingCaches call below so the
  // briefing_stages_editor query is updated via setQueryData (sync,
  // no refetch race) instead of invalidate-and-wait (which created a
  // one-tick window where the form re-rendered against the stale
  // cache before the refetch landed).
  const latestBriefingStagesSnapshot = useRef<Array<{
    id: string;
    label: string;
    event_date: string;
    display_order: number;
  }> | null>(null);

  // Stringified "last-saved" snapshots per state slice. saveRelatedData
  // consults these on entry to decide whether the scoped slice changed
  // since the previous save; if not it returns immediately without DB
  // round-trips. Initialised to empty strings so the very first save in
  // each scope always runs (no false negative when state was hydrated
  // from React Query before the snapshot mechanism could observe it).
  // Updated only at the tail of saveRelatedData on successful completion.
  const savedSnapshotsRef = useRef<ScopeStateSnapshot>({
    stages: "",
    timetable: "",
    crew: "",
    assignments: "",
    festivalDays: "",
  });

  // briefings.schedule_revision this editor's schedule (assignments + lineup)
  // was read at. A phone edit bumps it; every save checks it before its first
  // write (guardScheduleRevision, lib/saveFlow/scheduleRevision.ts). A ref:
  // the async save must read the latest value, and nothing renders it.
  const scheduleRevisionRef = useRef(createScheduleRevisionTracker());

  const [crewAssignments, setCrewAssignments] = useState<AssignmentFormData[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [aiDisclaimerOpen, setAiDisclaimerOpen] = useState(false);
  const [unassignedArtistsDialogOpen, setUnassignedArtistsDialogOpen] = useState(false);
  const [unassignedArtistsMessage, setUnassignedArtistsMessage] = useState("");
  const [emptyLineupDialogOpen, setEmptyLineupDialogOpen] = useState(false);
  const [lineupWarningMessage, setLineupWarningMessage] = useState("");
  const [usedAiExtraction, setUsedAiExtraction] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  // Crew member edit mode tracking (for published productions)
  const [editingCrewIds, setEditingCrewIds] = useState<Set<string>>(new Set());
  // Per-person card expand state, keyed by lower(email). Default
  // collapsed; auto-expanded on Add. Half-typed rows (blank email)
  // get a synthetic `__blank-${index}__` key.
  const [expandedCrewIds, setExpandedCrewIds] = useState<Set<string>>(new Set());

  // Date change warning dialog state
  const [dateChangeDialogOpen, setDateChangeDialogOpen] = useState(false);

  // Pending day-uncheck or person-removal confirm. Captures the ids
  // that would CASCADE-delete crew_assignments so the producer sees
  // an explicit count before the destructive action lands.
  const [pendingCrewRemoval, setPendingCrewRemoval] = useState<{
    kind: "day" | "person";
    label: string;
    assignmentCount: number;
    confirm: () => void;
  } | null>(null);

  // Track newly added crew members (e.g., after transfer) that need to be invited
  const [newlyAddedCrewMembers, setNewlyAddedCrewMembers] = useState<Set<string>>(new Set());

  // Step 8: Live Tracking Setup
  const [mapCenter, setMapCenter] = useState<[number, number]>([0, 0]);
  const [mapZoom, setMapZoom] = useState(15);
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [festivalBoundary, setFestivalBoundary] = useState<GeoJSON.Feature<GeoJSON.Polygon> | null>(
    null,
  );
  const [stageZones, setStageZones] = useState<GeoJSON.Feature<GeoJSON.Polygon>[]>([]);

  // Clear localStorage if this is a new production (no ID in URL)
  useEffect(() => {
    if (!currentBriefingId) {
      // This is a new production - clear any stored production state
      clearProductionState();
    }
  }, [currentBriefingId, clearProductionState]);

  // Use productionId from hook (handles localStorage fallback)
  const effectiveId = productionId || currentBriefingId;

  // Load existing briefing if editing - DEFENSE IN DEPTH: explicit producer_id filter
  const { data: existingBriefing, isFetching: existingBriefingFetching } = useQuery({
    queryKey: ["briefing", effectiveId],
    queryFn: async () => {
      if (!effectiveId || !userId) return null;

      // Scope enforced by RLS (owner / co-producer / admin).
      const { data, error } = await supabase
        .from("briefings")
        .select("*")
        .eq("id", effectiveId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveId && !!userId,
  });

  // Load team members for autocomplete
  const { data: teamMembers } = useQuery({
    queryKey: ["team-members", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("producer_id", user!.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Quick-add a crew person to My Team straight from the crew step.
  const { addToTeamMutation } = useTeamMembers();

  // Load producer profile data
  const { data: producerProfile } = useQuery({
    queryKey: ["producer-profile", userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Briefing guide: show 2 tips per step for first-time producers
  const BRIEFING_GUIDE_TIPS: Record<number, GuideStep[]> = {
    0: [
      {
        targetSelector: '[data-guide="step-0-tip-1"]',
        title: "Festival name",
        description: "Give your production a clear name your crew will recognize.",
      },
      {
        targetSelector: '[data-guide="step-0-tip-2"]',
        title: "Dates",
        description: "Set the start and end dates of your event.",
      },
    ],
    1: [
      {
        targetSelector: '[data-guide="step-1-tip-1"]',
        title: "Add stages",
        description:
          "Define each stage or area where your crew will work. Stages can differ per day if needed.",
      },
    ],
    2: [
      {
        targetSelector: '[data-guide="step-2-tip-1"]',
        title: "AI Upload vs Manual",
        description:
          "Use AI Upload to extract one stage's lineup per screenshot, Multi AI Upload to extract a whole day across all stages from a single screenshot, or switch to Manual Mode to enter set times by hand.",
      },
      {
        targetSelector: '[data-guide="step-2-tip-2"]',
        title: "Choose file",
        description:
          "When using AI Upload, select a screenshot of your lineup here. The AI will extract artist names and set times for you.",
      },
    ],
    3: [
      {
        targetSelector: '[data-guide="step-3-tip-1"]',
        title: "Add crew member",
        description:
          "Click here to add a new crew member with their name, email, role and arrival time.",
      },
      {
        targetSelector: '[data-guide="step-3-tip-2"]',
        title: "Save draft",
        description: "You can always save your progress as a draft and continue later.",
      },
    ],
    4: [
      {
        targetSelector: '[data-guide="step-4-tip-1"]',
        title: "Festival floorplan",
        description:
          "Upload a festival map image here so your crew can use it as a reference for navigation.",
      },
    ],
    5: [
      {
        targetSelector: '[data-guide="step-5-tip-1"]',
        title: "Generate with AI",
        description:
          "Automatically generate crew schedules based on your lineup and crew members. You can also re-generate with custom instructions.",
      },
      {
        targetSelector: '[data-guide="step-5-tip-2"]',
        title: "Select a day",
        description: "Switch between festival days to manage schedules for each day separately.",
      },
      {
        targetSelector: '[data-guide="step-5-tip-3"]',
        title: "Drag to assign",
        description:
          "Drag crew members from the bottom onto artist blocks to create assignments, or click a block to add filming times by hand. You can resize assignments by dragging their edges, and each panel by dragging its bottom edge (double-click to reset).",
      },
    ],
    6: [
      {
        targetSelector: '[data-guide="step-6-tip-1"]',
        title: "Navigation address",
        description:
          "This address is used for the live tracking map center and helps crew members navigate to the event location.",
      },
      {
        targetSelector: '[data-guide="step-6-tip-2"]',
        title: "Crew catering",
        description:
          "These are the catering time slots that will be shown in the crew briefing. They will not appear in the crew schedule.",
      },
      {
        targetSelector: '[data-guide="step-6-tip-3"]',
        title: "Producer information",
        description:
          "These details are pre-filled from your Settings. Update them there to have them ready for every new production.",
      },
    ],
    7: [
      {
        targetSelector: '[data-guide="step-7-tip-1"]',
        title: "Festival boundary",
        description:
          "Draw a generous boundary around the entire festival area. Crew location tracking only activates when they are within this zone. Double-click to close the shape.",
      },
      {
        targetSelector: '[data-guide="step-7-tip-2"]',
        title: "Stage zones",
        description:
          "Stage zone locations are used for analytics and proximity tracking. Crew members also see these stages in their app as a live floormap. Double-click to close each zone.",
      },
    ],
    8: [
      {
        targetSelector: '[data-guide="step-8-tip-1"]',
        title: "Download Briefing (PDF)",
        description:
          "Export the briefing as a PDF for crew members who don't have access to the app. All information is also available in the app.",
      },
      {
        targetSelector: '[data-guide="step-8-tip-2"]',
        title: "Publish and invite crew",
        description:
          "Once published, this production goes live and your crew members will be able to access their briefing and schedule.",
      },
    ],
  };

  const briefingGuideSteps = BRIEFING_GUIDE_TIPS[currentStep] || [];

  // Show briefing guide when step changes and guide not completed
  useEffect(() => {
    if (
      producerProfile &&
      !(producerProfile as Tables<"profiles">).briefing_guide_completed &&
      !briefingGuideShownForStep.has(currentStep) &&
      (!currentBriefingId || currentStep > 0) // Show on new productions and subsequent steps
    ) {
      const timer = setTimeout(() => {
        setShowBriefingGuide(true);
        setBriefingGuideShownForStep((prev) => new Set(prev).add(currentStep));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep, producerProfile, currentBriefingId, briefingGuideShownForStep]);

  const handleBriefingGuideComplete = async () => {
    setShowBriefingGuide(false);
    // Mark as completed so it doesn't show again on next production
    if (!(producerProfile as Tables<"profiles">)?.briefing_guide_completed) {
      if (userId) {
        await supabase.from("profiles").update({ briefing_guide_completed: true }).eq("id", userId);
      }
    }
  };

  // Load lineup data for existing briefing, with the schedule revision it
  // was read at. Its own key: ["lineup", id] is shared with pages that cache
  // the bare rows, and a slice must never pair with another read's revision.
  const { data: existingLineupRead } = useQuery({
    queryKey: ["lineup_editor", effectiveId],
    queryFn: async () => {
      if (!effectiveId) throw new Error("No production to load the lineup of");
      return readWithScheduleRevision(
        effectiveId,
        () => readScheduleRevision(effectiveId),
        async () => {
          const { data, error } = await supabase
            .from("lineup")
            .select("*")
            .eq("briefing_id", effectiveId)
            .order("set_start");
          if (error) throw error;
          return data;
        },
      );
    },
    enabled: !!effectiveId,
  });

  // Load crew members for existing briefing
  const { data: existingCrew } = useQuery({
    queryKey: ["crew_members", effectiveId],
    queryFn: async () => {
      if (!effectiveId) return [];
      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("briefing_id", effectiveId);
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveId,
  });

  // Assign consistent colors to crew members with same name+email across days
  // Only run once when crew is loaded from database
  useEffect(() => {
    if (crew.length === 0 || !existingCrew || existingCrew.length === 0) return;

    // Check if colors are already assigned from database
    const hasColorsFromDb = crew.some((m) => m.color && m.hasColor);
    if (hasColorsFromDb) return; // Don't reassign if we have colors from DB

    // Group crew members by name+email
    const crewGroups = new Map<string, CrewMember[]>();
    crew.forEach((member) => {
      const key = `${member.name.toLowerCase()}-${member.email.toLowerCase()}`;
      if (!crewGroups.has(key)) {
        crewGroups.set(key, []);
      }
      crewGroups.get(key)!.push(member);
    });

    // Assign same color to members in the same group
    let colorIndex = 0;
    const updatedCrew = [...crew];
    let hasChanges = false;

    crewGroups.forEach((members) => {
      // Always assign a color to each group
      const memberWithColor = members.find((m) => m.hasColor && m.color);
      const assignedColor =
        memberWithColor?.color || DEFAULT_COLORS[colorIndex % DEFAULT_COLORS.length];

      members.forEach((member) => {
        const index = updatedCrew.findIndex((c) => c === member);
        if (index !== -1) {
          if (!updatedCrew[index].hasColor || !updatedCrew[index].color) {
            updatedCrew[index] = {
              ...updatedCrew[index],
              hasColor: true,
              color: assignedColor,
            };
            hasChanges = true;
          }
        }
      });

      if (!memberWithColor) {
        colorIndex++;
      }
    });

    if (hasChanges) {
      setCrew(updatedCrew);
    }
  }, [existingCrew?.length]); // Only run when crew is loaded from database

  // Load crew assignments for existing briefing, with the schedule revision
  // they were read at (editor-only key; same reason as the lineup above).
  const { data: existingAssignmentsRead } = useQuery({
    queryKey: ["crew_assignments_editor", effectiveId],
    queryFn: async () => {
      if (!effectiveId) throw new Error("No production to load the schedule of");
      return readWithScheduleRevision(
        effectiveId,
        () => readScheduleRevision(effectiveId),
        async () => {
          // Embed briefing_stages.label so the editor's CrewSchedule
          // and downstream consumers resolve stage names via the FK.
          // Exclude server-owned mirror rows (dedicated-assistant copies). They are
          // rebuilt by replace_crew_assignments on save; keeping them out of the
          // editor means they never round-trip back as producer-authored rows.
          const { data, error } = await supabase
            .from("crew_assignments")
            .select("*, briefing_stages(label)")
            .eq("briefing_id", effectiveId)
            .eq("is_active", true)
            .is("mirrored_from_id", null);
          if (error) throw error;
          return data;
        },
      );
    },
    enabled: !!effectiveId,
  });

  // Load live tracking data for existing briefing
  const { data: existingMapSettings } = useQuery({
    queryKey: ["map_settings", effectiveId],
    queryFn: async () => {
      if (!effectiveId) return null;
      const { data, error } = await supabase
        .from("map_settings")
        .select("*")
        .eq("briefing_id", effectiveId)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!effectiveId,
  });

  const { data: existingFestivalAreas, refetch: refetchFestivalAreas } = useQuery({
    queryKey: ["festival_areas", effectiveId],
    queryFn: async () => {
      if (!effectiveId) return [];
      const { data, error } = await supabase
        .from("festival_areas")
        .select("*")
        .eq("briefing_id", effectiveId);
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveId,
  });

  // Per-day doors overrides. The form needs these on load so reopening
  // a multi-day briefing surfaces the producer's per-day windows
  // (otherwise every day looked uniform = the briefing-level fallback,
  // and saving would overwrite the saved overrides with that uniform
  // value). Declared in productionQueries so the reader, the route
  // prefetcher and the create path's cache seed all agree on the key.
  const { data: existingDayDoors } = useQuery({
    ...briefingDayDoorsQuery(effectiveId),
    enabled: !!effectiveId,
  });

  // Canonical stage records — the post-canonical-migration source of
  // truth. Used to rebuild the editor's `stages` array when the
  // legacy `briefings.stages` JSONB has drifted (Test Festival Live
  // on staging had `name="Main Stage"` instead of `name="day0_stage1"`,
  // which made step 1's `startsWith("dayN_")` filter return nothing).
  const { data: existingBriefingStages = [], isFetching: existingBriefingStagesFetching } =
    useQuery({
      queryKey: ["briefing_stages_editor", effectiveId],
      queryFn: async () => {
        if (!effectiveId) return [];
        // Per-day briefing_stages: one row per (event_date, display_order).
        // Each row carries its own label; multi-day stages with the
        // same label are stored as separate rows. No event_dates[]
        // array, no per-day overrides — that architecture was dropped
        // in 20260506001300 (cascade-soup root cause).
        const { data, error } = await supabase
          .from("briefing_stages")
          .select("id, label, event_date, display_order")
          .eq("briefing_id", effectiveId)
          .order("event_date", { ascending: true })
          .order("display_order", { ascending: true });
        if (error) throw error;
        return data ?? [];
      },
      enabled: !!effectiveId,
    });
  useEffect(() => {
    if (existingBriefing) {
      setFestivalName(existingBriefing.festival_name || "");
      setLocation(existingBriefing.location || "");
      setTimezone(existingBriefing.timezone || DEFAULT_TIMEZONE);
      setFestivalDateStart(existingBriefing.festival_date_start || "");
      setFestivalDateEnd(existingBriefing.festival_date_end || "");
      setNavigationAddress(existingBriefing.navigation_address || "");
      setHotelAddress(existingBriefing.hotel_address || "");
      setFlightsEnabled(existingBriefing.flights_enabled ?? false);
      setArrivalTime(existingBriefing.arrival_time || "");
      setClientName(existingBriefing.client_name || "");
      setCrewDresscode(existingBriefing.crew_dresscode || "");
      setCrewCheckin(existingBriefing.crew_checkin || "");
      setCameraSettings(cameraSettingsText(existingBriefing.camera_settings));
      setAudioSettings(existingBriefing.audio_settings || "");
      setExtraSettings(existingBriefing.extra_settings || "");
      setRestrictions(
        existingBriefing.restrictions && (existingBriefing.restrictions as Restrictions)?.text
          ? ((existingBriefing.restrictions as Restrictions).text ?? "")
          : "",
      );
      setGeneralInstructions(existingBriefing.general_instructions || "");
    }
  }, [existingBriefing]);

  // Load producer profile data into step 7 fields
  useEffect(() => {
    if (producerProfile) {
      if (!producerPhone && producerProfile.phone) {
        setProducerPhone(producerProfile.phone);
      }
      if (!invoiceInformation && producerProfile.invoice_information) {
        setInvoiceInformation(producerProfile.invoice_information);
      }
      // Pre-fill company name from producer settings if not already set
      if (!clientName && producerProfile.company_name) {
        setClientName(producerProfile.company_name);
      }
    }
  }, [producerProfile]);

  useEffect(() => {
    // Wait for both source queries to be settled before synthesising
    // stages. Without this gate the editor briefly flickers to the
    // old stage labels after a save: react-query refetches `briefing`
    // and `briefing_stages_editor` independently, the briefing query
    // typically resolves first → useEffect fires with NEW briefing
    // but STALE briefing_stages → setStages renders the old labels
    // for one tick before the second refetch lands. By skipping the
    // sync while either query is fetching we always run on a
    // consistent snapshot.
    if (existingBriefingFetching || existingBriefingStagesFetching) return;

    if (existingBriefing) {
      // Stages source-of-truth: briefing_stages table when available
      // (post-migration 20260429120000). The legacy briefings.stages
      // JSONB is kept on disk for back-compat but can drift from the
      // canonical relational shape — Test Festival Live on staging
      // had names="Main Stage" instead of "dayN_stageM" which made
      // the editor's step-1 filter return empty.
      //
      // Per-day briefing_stages → editor's day-prefixed slug shape.
      // 1:1 mapping: each row is one (event_date, display_order)
      // entry, mapped onto `dayN_stageM` where N is the date's index
      // in festival days and M is display_order + 1.
      const startDate = existingBriefing.festival_date_start;
      const endDate = existingBriefing.festival_date_end;
      let parsed: Stage[] | null = null;
      if (existingBriefingStages.length > 0 && startDate && endDate) {
        const dayDates: string[] = [];
        const cursor = new Date(`${startDate}T00:00:00Z`);
        const end = new Date(`${endDate}T00:00:00Z`);
        while (cursor.getTime() <= end.getTime()) {
          dayDates.push(cursor.toISOString().slice(0, 10));
          cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
        const synthesised: Stage[] = [];
        for (let dayIdx = 0; dayIdx < dayDates.length; dayIdx++) {
          const date = dayDates[dayIdx];
          const stagesForDay = existingBriefingStages
            .filter((s) => (s as typeof s & { event_date?: string }).event_date === date)
            .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
          for (let stageIdx = 0; stageIdx < stagesForDay.length; stageIdx++) {
            synthesised.push({
              name: `day${dayIdx}_stage${stageIdx + 1}`,
              label: stagesForDay[stageIdx].label,
            });
          }
        }
        if (synthesised.length > 0) parsed = synthesised;
      }
      if (!parsed && existingBriefing.stages && Array.isArray(existingBriefing.stages)) {
        parsed = parseStages(existingBriefing.stages) as Stage[];
      }
      if (parsed) {
        setStages(parsed);
        setOriginalStageLabels(new Map(parsed.map((s) => [s.name, s.label])));
      }
      if (existingBriefing.custom_subjects && Array.isArray(existingBriefing.custom_subjects)) {
        setCustomSubjects(parseCustomSubjects(existingBriefing.custom_subjects) as CustomSubject[]);
      }
      setCustomFields(parseCustomFields(existingBriefing.custom_fields));
      setVideoExamples(parseVideoExamples(existingBriefing.video_examples));
      if (existingBriefing.catering) {
        const catering = existingBriefing.catering as CateringSettings;
        setLunchEnabled(!!catering.lunch?.enabled);
        setDinnerEnabled(!!catering.dinner?.enabled);
        setNightsnackEnabled(!!catering.nightsnack?.enabled);

        setLunch(catering.lunch || { start: "12:00", end: "14:00" });
        setDinner(catering.dinner || { start: "18:00", end: "20:00" });
        setNightsnack(catering.nightsnack || { start: "02:00", end: "04:00" });
      }

      setOthers(existingBriefing.others || "");
      setMapStoragePath(
        (existingBriefing as { map_storage_path?: string | null }).map_storage_path ?? null,
      );

      // Auto-populate Doors Open/Close from database if available
      // Don't overwrite from festivalDays as those might be user-edited
    }
  }, [
    existingBriefing,
    existingBriefingStages,
    existingBriefingFetching,
    existingBriefingStagesFetching,
  ]);

  // Furthest step the producer may navigate to from the step nav.
  // Advances ONLY as they move forward through the wizard — and the
  // Next button validates each step before advancing — so the nav is
  // strictly step-by-step and can never skip a step's required info.
  // Backward navigation to any already-reached step stays free.
  const [maxStepReached, setMaxStepReached] = useState(initialStep);

  // Grow (never shrink) the unlocked range from three sources: the step
  // the producer is on now, how far they'd previously got on an existing
  // production (last_edit_step), and "all steps" for a published one
  // (fully done → free editing). Never shrinks, so hopping back and forth
  // between reached steps keeps them all open.
  useEffect(() => {
    setMaxStepReached((m) => {
      let next = Math.max(m, currentStep);
      if (existingBriefing) {
        next = Math.max(
          next,
          existingBriefing.status === "published"
            ? STEPS.length - 1
            : (existingBriefing.last_edit_step ?? 0),
        );
      }
      return next;
    });
  }, [currentStep, existingBriefing]);

  // Step-nav click: save the current step's section, then jump. Going
  // back or to an already-completed step never re-validates (the data
  // is already there); the gating lives in WizardStepper.
  const handleStepNavClick = async (target: number) => {
    if (target === currentStep) return;
    // Defence in depth: never jump to a still-locked step even if a
    // stale button slips through.
    if (target > maxStepReached) return;
    try {
      await handleSave(
        existingBriefing?.status === "published" ? "published" : "draft",
        false,
        stepScope(currentStep),
      );
    } catch (error) {
      console.error(error);
    }
    setCurrentStep(target);
  };

  // Track if festival days have been initialized from database (separate effect)
  const [hasInitializedDays, setHasInitializedDays] = useState(false);
  // Set while this editor inserts a production of its own, then to that
  // production's id. Such a production has nothing on the server to
  // hydrate from — the form in front of the producer IS the source of
  // truth — and the hydration effect below must not act on the reads
  // that publishing the new id sets off.
  //
  // A ref, not state: createProduction publishes the new id
  // synchronously, so the guard has to be visible before React gets a
  // chance to render, and it must survive the reconciliation that
  // navigating from /briefings/create to /briefings/:id/edit performs.
  //
  // It holds the id rather than a flag so the guard is scoped to the
  // one production it was created for. Navigating on from here to a
  // DIFFERENT production reconciles the same component, and that one
  // does have server state to hydrate from.
  const locallyCreatedRef = useRef<"pending" | string | null>(null);

  useEffect(() => {
    // Only reconstruct days once on initial load, never on date changes.
    // Wait for both the briefing AND the per-day doors query to resolve
    // (existingDayDoors is undefined while loading, [] when fetched and
    // empty) so we don't fire twice — first with the fallback uniform
    // values and then with the real per-day overrides on top.
    if (
      existingBriefing &&
      !hasInitializedDays &&
      locallyCreatedRef.current !== "pending" &&
      locallyCreatedRef.current !== existingBriefing.id &&
      existingBriefing.festival_date_start &&
      existingBriefing.festival_date_end &&
      existingDayDoors !== undefined
    ) {
      const days = reconstructFestivalDays({
        startDate: existingBriefing.festival_date_start,
        endDate: existingBriefing.festival_date_end,
        perDayDoors: existingDayDoors,
        fallback: {
          doors_open: existingBriefing.doors_open ?? null,
          doors_close: existingBriefing.doors_close ?? null,
        },
      });

      // Form-only UI default: <Input type="time"> needs a value, and
      // 12:00 / 23:00 mirrors the new-briefing default at the top of
      // this file. Apply here, not in the shared helper, so the
      // helper's "no data" stays "no data" for display readers.
      // Re-attach any custom day name stored under this date.
      const savedDayNames =
        existingBriefing.day_names && typeof existingBriefing.day_names === "object"
          ? (existingBriefing.day_names as Record<string, unknown>)
          : {};
      const daysWithFormDefaults = days.map((d) => ({
        ...d,
        startTime: d.startTime || "12:00",
        endTime: d.endTime || "23:00",
        name: typeof savedDayNames[d.date] === "string" ? (savedDayNames[d.date] as string) : "",
      }));

      if (daysWithFormDefaults.length > 0) {
        setFestivalDays(daysWithFormDefaults);
        setHasInitializedDays(true);
        // Seed the save baseline with what we just hydrated, so simply
        // opening a production and clicking Next doesn't re-upsert the
        // doors it just read. It also keeps the form-only 12:00/23:00
        // fillers above out of the database — the producer never typed
        // them, so an untouched day stays NULL.
        savedSnapshotsRef.current = {
          ...savedSnapshotsRef.current,
          festivalDays: JSON.stringify(daysWithFormDefaults),
        };
      }
    }
  }, [existingBriefing, existingDayDoors, hasInitializedDays]);

  // Load lineup data. The timetable is replaced by every read with NEW rows
  // (shouldAdoptScheduleRead: not the rows it took last, never after a stop);
  // the tracker then takes that read's revision (also when both are empty).
  const adoptedLineupRowsRef = useRef<NonNullable<typeof existingLineupRead>["rows"] | null>(null);
  useEffect(() => {
    if (!existingLineupRead) return;
    if (
      !shouldAdoptScheduleRead(
        scheduleRevisionRef.current,
        existingLineupRead,
        adoptedLineupRowsRef.current,
      )
    ) {
      return;
    }
    const existingLineup = existingLineupRead.rows;
    if (existingLineup.length === 0 && timetable.length > 0) return; // local entries, not this read
    adoptedLineupRowsRef.current = existingLineup;
    if (existingLineup.length > 0) {
      const loadedTimetable: TimetableEntry[] = existingLineup
        .map((entry) => ({
          id: entry.id,
          stage_name: entry.stage_name,
          event_date: entry.event_date,
          artist_name: entry.artist_name,
          set_start: entry.set_start,
          set_end: entry.set_end,
          highlighted: entry.highlighted ?? false,
        }))
        .sort((a, b) => compareSetTimes(a.set_start, b.set_start));
      setTimetable(loadedTimetable);
    }
    scheduleRevisionRef.current.hydrated(
      existingLineupRead.briefingId,
      "lineup",
      existingLineupRead.revision,
    );
  }, [existingLineupRead]);

  // Load crew members - with duplicate prevention
  // Determine if production is published (for locking crew members)
  const isPublished = existingBriefing?.status === "published";

  useEffect(() => {
    if (existingCrew && existingCrew.length > 0) {
      // Remove duplicates based on name, email, function, and event_date
      const uniqueCrewMap = new Map<string, (typeof existingCrew)[0]>();
      existingCrew.forEach((member) => {
        const key = `${member.name.trim()}-${member.email.trim()}-${member.function}-${member.event_date}`;
        if (!uniqueCrewMap.has(key)) {
          uniqueCrewMap.set(key, member);
        }
      });

      const uniqueCrew = Array.from(uniqueCrewMap.values());
      // Assign a stable client-side _personId per unique lower(email).
      // The editor's collapsible card group key uses this so typing in
      // the email field doesn't split a half-typed group on every
      // keystroke. Same email across days → same _personId, so a
      // role/phone edit propagates correctly.
      const personIdByEmail = new Map<string, string>();
      const loadedCrew: CrewMember[] = uniqueCrew.map((member, index) => {
        const emailKey = (member.email ?? "").trim().toLowerCase();
        let personId = emailKey ? personIdByEmail.get(emailKey) : undefined;
        if (!personId) {
          personId =
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `pid-${Math.random().toString(36).slice(2)}-${index}`;
          if (emailKey) personIdByEmail.set(emailKey, personId);
        }
        return {
          id: member.id,
          _personId: personId,
          name: member.name,
          email: member.email,
          phone: member.phone || "",
          function: member.function,
          arrival_time: member.arrival_time || "",
          event_date: member.event_date,
          camera_id: member.camera_id || "",
          color: member.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
          hasColor: true,
        };
      });
      setCrew(loadedCrew);

      // Store original crew data for tracking changes (use database ID as key)
      const originalMap = new Map<string, CrewMember>();
      loadedCrew.forEach((member) => {
        if (member.id) {
          originalMap.set(member.id, { ...member });
        }
      });
    }
  }, [existingCrew]);

  // Load crew assignments - only on initial load when empty, with deduplication.
  // Only then does the local schedule equal this read, so only then does the
  // tracker take its revision: a later (fresher) read is ignored, and so must
  // its revision be.
  useEffect(() => {
    if (!existingAssignmentsRead || crewAssignments.length > 0) return;
    const existingAssignments = existingAssignmentsRead.rows;
    if (existingAssignments.length > 0) {
      // Deduplicate assignments based on unique key (prevents rendering duplicates from DB)
      const uniqueMap = new Map<string, (typeof existingAssignments)[0]>();
      existingAssignments.forEach((assignment) => {
        const key = `${assignment.crew_member_id}-${assignment.event_date}-${assignment.artist_name || ""}-${assignment.stage_name}-${assignment.filming_start}-${assignment.filming_end}`;
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, assignment);
        }
      });
      setCrewAssignments(
        Array.from(uniqueMap.values()).map((a) => ({
          ...a,
          crew_member_id: a.crew_member_id ?? "",
          assignment_type: a.assignment_type as "artist" | "custom",
          location: a.location || parseLocationFromStageName(a.stage_name, a.assignment_type),
        })) as AssignmentFormData[],
      );
    }
    scheduleRevisionRef.current.hydrated(
      existingAssignmentsRead.briefingId,
      "assignments",
      existingAssignmentsRead.revision,
    );
  }, [existingAssignmentsRead]);

  // Load live tracking data
  useEffect(() => {
    if (existingMapSettings) {
      setMapCenter([existingMapSettings.center_longitude, existingMapSettings.center_latitude]);
      setMapZoom(existingMapSettings.zoom_level ?? 15);
      setIsMapLocked(existingMapSettings.is_locked || false);
    }
  }, [existingMapSettings]);

  useEffect(() => {
    if (existingFestivalAreas && existingFestivalAreas.length > 0) {
      const boundary = existingFestivalAreas.find((area) => area.area_type === "festival_boundary");
      if (boundary) {
        setFestivalBoundary(parseGeoFeature(boundary.polygon) as GeoJSON.Feature<GeoJSON.Polygon>);
      }

      const zones = existingFestivalAreas
        .filter((area) => area.area_type === "stage_zone")
        .map((area): GeoJSON.Feature<GeoJSON.Polygon> => {
          const parsed = parseGeoFeature(area.polygon);
          return {
            type: "Feature",
            properties: {
              ...parsed?.properties,
              stage_id: area.stage_id,
              day_index: area.day_index,
            },
            geometry: (parsed?.geometry ?? { type: "Polygon", coordinates: [] }) as GeoJSON.Polygon,
          };
        });

      if (zones.length > 0) {
        setStageZones(zones);
      }
    }
  }, [existingFestivalAreas]);

  // DISABLED: Auto-save between steps - we only save on Next/Previous/Save button clicks now
  // This prevents constant saving that causes UI flicker and performance issues
  /*
  useEffect(() => {
    if (!productionId || isSaving || currentStep === 6) {
      return;
    }

    const briefingData = getBriefingData();
    scheduleAutoSave(briefingData);

    return () => {
      // Cleanup handled by hook
    };
  }, [
    festivalName,
    location,
    festivalDays,
    stages,
    customSubjects,
    navigationAddress,
    arrivalTime,
    clientName,
    crewDresscode,
    crewCheckin,
    cameraSettings,
    audioSettings,
    extraSettings,
    restrictions,
    generalInstructions,
    others,
    lunchEnabled,
    dinnerEnabled,
    nightsnackEnabled,
    lunch,
    dinner,
    nightsnack,
    mapStoragePath,
    mapCenter,
    mapZoom,
    festivalBoundary,
    stageZones,
    currentStep,
    productionId,
    isSaving,
    scheduleAutoSave,
  ]);
  */
  // Helper to build briefing data from current state
  const getBriefingData = () => ({
    festival_name: festivalName,
    location: location,
    timezone: timezone,
    festival_date_start: festivalDays.length > 0 ? festivalDays[0].date : null,
    festival_date_end: festivalDays.length > 0 ? festivalDays[festivalDays.length - 1].date : null,
    doors_open: festivalDays.length > 0 ? festivalDays[0].startTime : null,
    doors_close: festivalDays.length > 0 ? festivalDays[0].endTime : null,
    // Custom per-day names, keyed by event_date. Only non-empty names
    // are stored; blank days fall back to the date at display time.
    day_names: festivalDays.reduce<Record<string, string>>((acc, d) => {
      const name = (d.name ?? "").trim();
      if (name) acc[d.date] = name;
      return acc;
    }, {}),
    navigation_address: navigationAddress,
    hotel_address: hotelAddress,
    // flights_enabled is deliberately NOT written here: FlightsSection
    // saves the toggle the moment it is flipped. Writing it from this
    // payload too meant a later step save, built from a snapshot taken
    // before the flip, could silently revert it.
    arrival_time: arrivalTime || null,
    client_name: clientName,
    map_storage_path: mapStoragePath,
    last_edit_step: currentStep,
    activated_crew_ids: Array.from(
      crewAssignments.reduce((acc, assignment) => {
        acc.add(assignment.crew_member_id);
        return acc;
      }, new Set<string>()),
    ),
    stages: JSON.parse(JSON.stringify(stages)),
    custom_subjects: JSON.parse(JSON.stringify(customSubjects)),
    // Producer-defined extra briefing cards. Drop fully-empty rows so a
    // blank editor row never persists as a ghost card for the crew.
    custom_fields: JSON.parse(JSON.stringify(visibleEditorCustomFields)),
    video_examples: JSON.parse(JSON.stringify(filledEditorVideoExamples)),
    catering: JSON.parse(
      JSON.stringify({
        lunch: lunchEnabled ? { ...lunch, enabled: true } : null,
        dinner: dinnerEnabled ? { ...dinner, enabled: true } : null,
        nightsnack: nightsnackEnabled ? { ...nightsnack, enabled: true } : null,
      }),
    ),
    crew_dresscode: crewDresscode,
    crew_checkin: crewCheckin,
    camera_settings: cameraSettings ? { text: cameraSettings } : null,
    audio_settings: audioSettings,
    extra_settings: extraSettings,
    restrictions: restrictions ? { text: restrictions } : null,
    general_instructions: generalInstructions,
    others: others,
    // Note: Live tracking data is stored separately in map_settings and festival_areas tables
    // via saveLiveTrackingData() function, not in the briefings table
  });

  // The slices saveRelatedData writes for `scope`, as the saved-snapshot
  // strings its skip test compares. Only the slices the scope can actually
  // inspect are stringified — saves ~ms-scale CPU per Next/Back click on
  // large lineups where most navigations don't touch most slices.
  const relatedDataSnapshot = (scope: SaveScope): ScopeStateSnapshot => {
    const { runStages, runLineup, runCrew, runDays } = computeScopeFlags(scope);
    return {
      stages: runStages ? JSON.stringify(stages) : "",
      timetable: runLineup ? JSON.stringify(timetable) : "",
      crew: runCrew ? JSON.stringify(crew) : "",
      // Crew schedule — owned by the crew scope (rationale in saveScope.ts).
      assignments: runCrew ? JSON.stringify(crewAssignments) : "",
      festivalDays: runDays ? JSON.stringify(festivalDays) : "",
    };
  };

  // How a save stopped by a phone edit tells the producer, once per stop:
  // guardScheduleRevision and saveRelatedData's checked writes call it; the
  // save's callers (handleSave, the step-5 onSave) don't show it again.
  const notifyScheduleStop = (message: string) => sonnerToast.error(message);

  // Run BEFORE a save's first write (map upload, briefing row, profile,
  // related data): did a phone edit the schedule since this editor read it?
  // Then the save stops right here — nothing written, local edits kept —
  // instead of silently reverting the phone. Never retried on a conflict.
  // Checks only when saveRelatedData would write anything for `scope` (its
  // own skip test), so saving other fields is never blocked. Resolves with
  // the revision it checked (null: nothing to check) for saveRelatedData.
  const guardScheduleRevision = async (scope: SaveScope): Promise<number | null> => {
    const briefingId = currentBriefingId;
    if (!briefingId) return null;
    if (!isScopeDirty(scope, relatedDataSnapshot(scope), savedSnapshotsRef.current)) return null;
    return checkScheduleRevision(
      supabase,
      scheduleRevisionRef.current,
      briefingId,
      notifyScheduleStop,
    );
  };

  // Handler for deleting a crew member (removes member and their assignments)
  // Save all related data (lineup, crew, assignments).
  //
  // `baselineStagesSnapshot`: the DB's view of briefings.stages
  // sampled BEFORE saveProduction overwrote the JSONB. handleSave
  // takes the snapshot at the very top so we have a stable ground
  // truth for the rename map regardless of React-state staleness
  // or refetch races. Falls back to React's originalStageLabels
  // when the snapshot wasn't supplied (legacy callers).
  //
  // `checkedScheduleRevision`: what guardScheduleRevision(scope) resolved
  // with — the caller runs it before ITS first write. The lineup, shift and
  // shift-time writes send it along and the database checks it again
  // atomically with each write: a phone edit since then throws
  // ScheduleChangedElsewhereError (shown once; nothing of that write written,
  // local edits kept and unsaved, never retried).
  const saveRelatedData = async (
    baselineStagesSnapshot: Array<{ name: string; label: string }> | undefined,
    scope: SaveScope,
    checkedScheduleRevision: number | null,
  ): Promise<void> => {
    // Use the URL briefing ID directly - single source of truth
    const briefingId = currentBriefingId;
    if (!briefingId) {
      return;
    }

    // Per-step scoping. The producer editor's Next/Back buttons pass
    // only the scope of the step being left so we don't rewrite
    // unrelated tables on every navigation. "all" stays for explicit
    // Save-draft and Publish buttons. The cascade dependencies —
    // stages → lineup, lineup → crew_assignments time sync — are
    // handled at the section boundaries below.
    const { runStages, runLineup, runCrew, runDays } = computeScopeFlags(scope);

    // Skip the entire save if nothing in this scope changed since the
    // last successful save. Producers frequently click Next → Back →
    // Next to compare steps; without this short-circuit each round-
    // trip re-runs every section's SELECTs (and, pre-RPC-diff-check,
    // re-UPDATEs every row). The snapshot is refreshed at the tail
    // of this function so only the slices we actually saved get
    // marked clean.
    const currentSnapshot = relatedDataSnapshot(scope);
    if (!isScopeDirty(scope, currentSnapshot, savedSnapshotsRef.current)) {
      return;
    }

    // Build stage rename map: oldLabel → newLabel (by matching on stable stage.name key)
    const baseline =
      baselineStagesSnapshot ??
      Array.from(originalStageLabels, ([name, label]) => ({ name, label }));
    const stageRenameMap = buildStageRenameMap({
      baseline,
      current: stages.map((s) => ({ name: s.name, label: s.label })),
    });
    const resolveStage = (name: string) => stageRenameMap.get(name) ?? name;

    // Stage rename propagation runs at the DB layer:
    //   • briefing_stages.label UPDATE → trigger sync_stage_name_on_label_change
    //     cascades to lineup.stage_name, crew_assignments.stage_name,
    //     festival_areas.name (migrations 20260429120200 + 20260503150000).
    //   • For renames that go DELETE+INSERT instead of UPDATE (e.g.
    //     when the upsert key (briefing_id,label) doesn't match), the
    //     ON DELETE SET NULL clears stage_id; the BEFORE INSERT
    //     trigger lineup_set_stage_id / crew_assignments_set_stage_id
    //     (migration 20260504240000) re-resolves it from the new label
    //     when replaceLineup / replace_crew_assignments write the rows
    //     below.
    // No client-side cascade UPDATE needed; the previous attempt left
    // crew_assignments in a stage_name=new / stage_id=stale state that
    // FK-violated on the next save.

    // Mirror the producer's stages state into the canonical
    // briefing_stages table. The legacy day-suffixed UI in this file
    // still drives the source of truth in local React state; this
    // projection is what makes the new schema usable by the rest of
    // the app (analytics rollup, custom-assignment dropdown, etc.)
    // without rewriting the editor JSX in this same commit.
    // Idempotent via the (briefing_id, label) UNIQUE constraint.
    // Per-day projection: each `dayN_stageM` slot in React state
    // becomes ONE briefing_stages row with a single event_date. id is
    // sent when known so the RPC takes the UPDATE-by-id path
    // (preserves FK identity through label edits — sync trigger
    // propagates to lineup/crew_assignments/festival_areas).
    //
    // Scope: only saves briefing_stages when scope includes stages.
    // resolveStageId is needed by lineup and crew sections regardless
    // — when stages is skipped this round, build it from the React
    // Query cache (no DB roundtrip) so the rename-aware lineup save
    // still lands rows on the correct per-day stage_id.
    let resolveStageId: ReturnType<typeof buildStageIdResolver>;

    if (runStages) {
      const festivalDates = festivalDays.map((d) => d.date);
      const slotToExistingId = new Map<string, string>();
      for (let dayIdx = 0; dayIdx < festivalDates.length; dayIdx++) {
        const date = festivalDates[dayIdx];
        const stagesForDay = existingBriefingStages
          .filter((s) => (s as typeof s & { event_date?: string }).event_date === date)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
        for (let i = 0; i < stagesForDay.length; i++) {
          slotToExistingId.set(`day${dayIdx}_stage${i + 1}`, stagesForDay[i].id);
        }
      }
      const desiredRows = stages
        .map((slot) => {
          const m = slot.name.match(/^day(\d+)_stage(\d+)$/);
          if (!m) return null;
          const dayIdx = parseInt(m[1], 10);
          const slotNum = parseInt(m[2], 10);
          if (!Number.isFinite(dayIdx) || dayIdx < 0 || dayIdx >= festivalDates.length) return null;
          if (!slot.label || !slot.label.trim()) return null;
          return {
            id: slotToExistingId.get(slot.name) ?? null,
            label: slot.label,
            event_date: festivalDates[dayIdx],
            display_order: slotNum - 1,
          };
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);

      const { data: stageRows, error: rbsErr } = await supabase.rpc("replace_briefing_stages", {
        p_briefing_id: briefingId,
        p_rows: desiredRows as Json,
      });
      if (rbsErr) throw rbsErr;
      type StageRpcRow = {
        out_id: string;
        out_label: string;
        out_location: string | null;
        out_event_date: string;
        out_display_order: number;
      };
      const stageRpcRows = (stageRows ?? []) as StageRpcRow[];
      // Per-day resolver: same label can appear on multiple days
      // (briefing_stages is per-day post-001300). A label-only Map.get
      // would return day-1's id for a day-2 row → corrupted lineup.stage_id
      // and crew_assignments.stage_id, which breaks the BEFORE-trigger
      // lineup_id auto-fill, which breaks the post-save sync's FK fast
      // path, which stretches assignments to the slot's full bounds.
      resolveStageId = buildStageIdResolver(
        stageRpcRows.map((r) => ({
          id: r.out_id,
          label: r.out_label,
          event_date: r.out_event_date,
        })),
      );
      // Snapshot for invalidateBriefingCaches' setQueryData hand-off,
      // so briefing_stages_editor cache lands without a refetch race.
      latestBriefingStagesSnapshot.current = stageRpcRows.map((r) => ({
        id: r.out_id,
        label: r.out_label,
        event_date: r.out_event_date,
        display_order: r.out_display_order,
      }));
    } else {
      // Stages save skipped this round (scope=lineup or scope=crew).
      // Build resolveStageId from the React Query cache so the
      // downstream sections still get per-day stage ids without a
      // DB roundtrip.
      resolveStageId = buildStageIdResolver(
        (existingBriefingStages ?? []).map((s) => ({
          id: s.id,
          label: s.label,
          event_date: (s as typeof s & { event_date?: string }).event_date ?? "",
        })),
      );
    }

    // Snapshot the lineup BEFORE we replace it. The post-save sync at
    // the tail of saveRelatedData uses this to distinguish between
    // assignments that were "following" their slot (and should track a
    // lineup move) and assignments the producer deliberately diverged
    // (e.g. resized to cover only part of the set — those must NOT be
    // widened back to the full slot on save).
    let previousLineupSnapshot: Array<{
      artist_name: string;
      stage_name: string;
      event_date: string;
      set_start: string;
      set_end: string;
    }> = [];
    if (runLineup && timetable.length > 0) {
      const { data: snap } = await supabase
        .from("lineup")
        .select("artist_name, stage_name, event_date, set_start, set_end")
        .eq("briefing_id", briefingId);
      previousLineupSnapshot = snap ?? [];
    }

    // Save lineup data — atomic replace via RPC. Replaces the previous
    // DELETE-then-INSERT pattern that wiped the producer's full
    // timetable on mid-save failure. See apps/web/src/lib/lineupSave.ts.
    if (runLineup && timetable.length > 0) {
      const lineupData: LineupRow[] = timetable.map((entry) => {
        const resolvedStage = resolveStage(entry.stage_name);
        return {
          // Pass the existing lineup.id so replace_lineup v3 UPDATEs
          // in place instead of DELETE+INSERT. Preserves the FK
          // target for crew_assignments.lineup_id and lets the
          // sync_artist_name_on_lineup_change trigger propagate
          // artist renames cleanly. Omit for brand-new rows.
          id: entry.id,
          briefing_id: briefingId,
          stage_name: resolvedStage,
          stage_id: resolveStageId(resolvedStage, entry.event_date),
          event_date: entry.event_date,
          artist_name: entry.artist_name,
          set_start: entry.set_start && entry.set_start.trim() !== "" ? entry.set_start : "",
          set_end: entry.set_end && entry.set_end.trim() !== "" ? entry.set_end : "",
          highlighted: entry.highlighted ?? false,
        };
      });
      await scheduleWriteAtRevision(scheduleRevisionRef.current, notifyScheduleStop, () =>
        replaceLineup(supabase, briefingId, lineupData, checkedScheduleRevision),
      );
    }

    // Save crew members - using ID-based updates to preserve assignments when name/email changes
    if (runCrew && crew.length > 0) {
      // Get existing crew members to preserve their IDs
      const { data: existingCrewMembers } = await supabase
        .from("crew_members")
        .select("id, name, email, event_date")
        .eq("briefing_id", briefingId);

      // Auto-link user_id to existing crew profiles. Single batch RPC
      // (post-migration 20260504241300) — was N parallel calls per
      // unique email, one per crew member; now one round-trip total.
      const crewEmails = [...new Set(crew.map((m) => m.email.toLowerCase()))];
      const emailToUserIdMap = new Map<string, string>();
      if (crewEmails.length > 0) {
        const { data: profileRows } = await supabase.rpc("lookup_profiles_by_emails", {
          lookup_emails: crewEmails,
        });
        for (const row of profileRows ?? []) {
          if (row?.email && row?.user_id) {
            emailToUserIdMap.set(row.email.toLowerCase(), row.user_id);
          }
        }
      }

      // Partition decisions are computed by the unit-tested helper
      // (apps/web/src/lib/saveFlow/partitionCrewSave.ts). The 14
      // vitest cases there pin id-match priority, key-fallback,
      // collision protection, email normalization, user_id
      // attachment, idsToDelete, and the per-day expansion contract.
      const partition = partitionCrewSave({
        briefingId,
        localCrew: crew.map((m) => ({
          id: m.id,
          name: m.name,
          email: m.email,
          phone: m.phone,
          function: m.function,
          arrival_time: m.arrival_time,
          event_date: m.event_date,
          color: m.color,
        })),
        existingCrew: existingCrewMembers ?? [],
        emailToUserId: emailToUserIdMap,
      });
      const crewToUpdate = partition.toUpdate;
      const crewToInsert = partition.toInsert;
      const crewIdMapping = partition.crewIdMapping;
      const usedDbIds = partition.usedDbIds;
      // Used by the post-insert state sync below to short-circuit
      // members whose id is already a DB id.
      const existingCrewIdSet = new Set((existingCrewMembers ?? []).map((e) => e.id));

      // Single atomic RPC replaces 3 DB calls (upsert toUpdate, upsert
      // toInsert with select, delete idsToDelete). Pass the union of
      // partitioned desired rows as p_rows; the RPC handles the same
      // two-tier matching (id → natural key) server-side and deletes
      // anything in DB not in the desired list. Returns the post-apply
      // crew_members rows so we can backfill crewIdMapping for newly-
      // inserted entries — preserving the local-id → db-id translation
      // that the assignments save flow depends on.
      const desiredCrew = [
        ...crewToUpdate,
        ...crewToInsert.map(({ _originalMemberId: _o, ...rest }) => rest),
      ];
      const { data: rcRows, error: rcErr } = await supabase.rpc("replace_crew", {
        p_briefing_id: briefingId,
        p_rows: desiredCrew as Json,
      });
      if (rcErr) throw rcErr;
      type RcRow = {
        out_id: string;
        out_name: string;
        out_email: string;
        out_event_date: string;
      };
      const rcReturned: RcRow[] = rcRows ?? [];

      // Backfill crewIdMapping for newly-inserted rows. Match by natural
      // key (name + lowercase email + event_date); claim each RPC-returned
      // row at most once.
      const claimableRpc = [...rcReturned];
      crewToInsert.forEach((member) => {
        const memberKey = `${member.name.trim()}_${member.email.trim().toLowerCase()}_${member.event_date}`;
        const idx = claimableRpc.findIndex(
          (r) =>
            `${r.out_name.trim()}_${r.out_email.trim().toLowerCase()}_${r.out_event_date}` ===
            memberKey,
        );
        if (idx >= 0) {
          const newDbId = claimableRpc[idx].out_id;
          claimableRpc.splice(idx, 1);
          if (member._originalMemberId) crewIdMapping.set(member._originalMemberId, newDbId);
          crewIdMapping.set(memberKey, newDbId);
          usedDbIds.add(newDbId);
        }
      });

      // Sync DB ids back to crew state so Step 6 (AI generation) works.
      setCrew((prevCrew) =>
        prevCrew.map((member) => {
          if (member.id && existingCrewIdSet.has(member.id)) return member;
          if (member.id && crewIdMapping.has(member.id)) {
            return { ...member, id: crewIdMapping.get(member.id)! };
          }
          const key = `${member.name.trim()}_${member.email.trim().toLowerCase()}_${member.event_date}`;
          const mappedId = crewIdMapping.get(key);
          if (mappedId) return { ...member, id: mappedId };
          return member;
        }),
      );

      // RPC handled deletes implicitly (anything not in p_rows). The
      // partition's idsToDelete is no longer needed.

      // Mirror local crew state into the canonical briefing_crew /
      // briefing_crew_days tables. Same pattern as the briefing_stages
      // mirror earlier in this function: legacy table stays the source
      // of truth for now, the new tables are a denormalized parallel
      // view that downstream consumers (analytics, reports) can
      // gradually migrate onto. Idempotent via the
      // (briefing_id, email) UNIQUE on briefing_crew and the
      // (briefing_crew_id, event_date) PK on briefing_crew_days.
      {
        const { crew: desiredCrew, days: desiredDays } = legacyCrewToRecords({
          briefingId,
          legacyRows: crew.map((m) => ({
            name: m.name,
            email: m.email,
            phone: m.phone || null,
            color: m.color || null,
            user_id: null,
            event_date: m.event_date,
            function: m.function,
            arrival_time: m.arrival_time && m.arrival_time.trim() !== "" ? m.arrival_time : null,
            camera_id: m.camera_id && m.camera_id.trim() !== "" ? m.camera_id : null,
          })),
        });

        if (desiredCrew.length > 0) {
          // Lowercase email on insert so the briefing_crew UNIQUE
          // (briefing_id, email) collapses case-different inputs.
          // The deletion + lookup loop above also lowercases, so the
          // round-trip is internally consistent.
          const { error: bcErr } = await supabase.from("briefing_crew").upsert(
            desiredCrew.map((r) => ({
              briefing_id: r.briefing_id,
              name: r.name,
              email: r.email.toLowerCase(),
              phone: r.phone,
              color: r.color,
              user_id: r.user_id,
            })),
            { onConflict: "briefing_id,email" },
          );
          if (bcErr) throw bcErr;
        }

        // Drop briefing_crew rows whose email no longer exists in the
        // local list. Lowercase both sides — case-different emails on
        // re-edit (`Foo@x.com` vs `foo@x.com`) would otherwise mark
        // the existing row as "removed" and DELETE it, cascading into
        // briefing_crew_days and any FK references. The DB UNIQUE
        // constraint on (briefing_id, email) is case-sensitive on
        // staging+prod so we must normalise here.
        // Single SELECT covers both the "find rows to delete" and the
        // "build email→id map for day-rows" needs. The map is derived by
        // filtering out the rows we just deleted — saves one round-trip
        // per save and removes a case-drift window between two queries.
        const desiredEmails = new Set(desiredCrew.map((r) => r.email.toLowerCase()));
        const { data: existingBriefingCrew } = await supabase
          .from("briefing_crew")
          .select("id, email")
          .eq("briefing_id", briefingId);
        const removedCrewIds = (existingBriefingCrew ?? [])
          .filter((row) => !desiredEmails.has((row.email ?? "").toLowerCase()))
          .map((row) => row.id);
        if (removedCrewIds.length > 0) {
          await supabase.from("briefing_crew").delete().in("id", removedCrewIds);
        }

        const removedIdSet = new Set(removedCrewIds);
        const bcRows = (existingBriefingCrew ?? []).filter((row) => !removedIdSet.has(row.id));
        const crewIdByEmail = new Map<string, string>(
          bcRows.map((r) => [(r.email ?? "").toLowerCase(), r.id]),
        );

        const dayPayload = desiredDays
          .map((d) => {
            // canonicalKey is already lowercased upstream by
            // legacyCrewToRecords; normalise defensively.
            const emailKey = d.canonicalKey.split(":")[1]?.toLowerCase() ?? "";
            const crewId = crewIdByEmail.get(emailKey);
            return crewId
              ? {
                  briefing_crew_id: crewId,
                  event_date: d.event_date,
                  function: d.function,
                  arrival_time: d.arrival_time,
                  // Canonical Camera ID store; a DB trigger mirrors this
                  // onto crew_members.camera_id for the mobile read path.
                  camera_id: d.camera_id,
                }
              : null;
          })
          .filter((d): d is NonNullable<typeof d> => d !== null);
        if (dayPayload.length > 0) {
          const { error: bcdErr } = await supabase
            .from("briefing_crew_days")
            .upsert(dayPayload, { onConflict: "briefing_crew_id,event_date" });
          if (bcdErr) throw bcdErr;
        }

        // Day rows for this briefing whose (crew_id, date) pair is no
        // longer in the local state get removed. Built per-crew so we
        // don't accidentally delete other briefings' rows.
        const desiredDayKeys = new Set(
          desiredDays.map((d) => {
            const id = crewIdByEmail.get(d.canonicalKey.split(":")[1]);
            return id ? `${id}:${d.event_date}` : "";
          }),
        );
        const briefingCrewIds = (bcRows ?? []).map((r) => r.id);
        if (briefingCrewIds.length > 0) {
          const { data: existingDays } = await supabase
            .from("briefing_crew_days")
            .select("briefing_crew_id, event_date")
            .in("briefing_crew_id", briefingCrewIds);
          const removed = (existingDays ?? []).filter(
            (d) => !desiredDayKeys.has(`${d.briefing_crew_id}:${d.event_date}`),
          );
          // Parallel fan-out: PostgREST doesn't support tuple-IN deletes
          // and building a composite .or() filter is fiddly, so just run
          // the eq+eq deletes concurrently. With HTTP/2 multiplexing this
          // collapses to roughly one wall-clock RTT.
          await Promise.all(
            removed.map((d) =>
              supabase
                .from("briefing_crew_days")
                .delete()
                .eq("briefing_crew_id", d.briefing_crew_id)
                .eq("event_date", d.event_date),
            ),
          );
        }
      }

      // ============================================
      // STEP 6: SAVE CREW ASSIGNMENTS (Clean Logic)
      // ============================================
      const validCrewMemberIds = new Set(crewIdMapping.values());

      // Map assignments to database crew member IDs
      const assignmentsData = crewAssignments
        .map((assignment) => {
          let finalCrewMemberId = assignment.crew_member_id;

          if (!validCrewMemberIds.has(assignment.crew_member_id)) {
            const mappedId = crewIdMapping.get(assignment.crew_member_id);
            if (mappedId) {
              finalCrewMemberId = mappedId;
            } else {
              const matchingCrew = crew.find((c) => c.id === assignment.crew_member_id);
              if (matchingCrew) {
                const key = `${matchingCrew.name.trim()}_${matchingCrew.email.trim()}_${matchingCrew.event_date}`;
                const keyMappedId = crewIdMapping.get(key);
                if (keyMappedId) {
                  finalCrewMemberId = keyMappedId;
                }
              }
            }
          }

          if (!finalCrewMemberId || !validCrewMemberIds.has(finalCrewMemberId)) {
            return null;
          }

          const eventDate =
            assignment.event_date || (festivalDays.length > 0 ? festivalDays[0].date : null);
          if (!eventDate) return null;

          // stage_id resolution order: prefer the freshly-upserted
          // briefing_stages.id (via stageIdByLabel) over assignment.stage_id
          // from React state. Stale stage_id values point at briefing_stages
          // rows that may have been deleted in the upsert+cleanup above
          // (ON DELETE SET NULL would have already nulled them at the DB
          // layer, but React state hasn't refreshed). The BEFORE trigger
          // crew_assignments_set_stage_id (migration 20260504240000) is a
          // safety net for NULLs but doesn't repair stale values.
          const resolvedStageName = resolveStage(assignment.stage_name);
          return {
            id: assignment.id || undefined, // Preserve ID for existing assignments
            briefing_id: briefingId,
            crew_member_id: finalCrewMemberId,
            artist_name: assignment.artist_name || null,
            subject: assignment.subject || null,
            stage_id: resolveStageId(resolvedStageName, eventDate) ?? assignment.stage_id ?? null,
            stage_name: resolvedStageName,
            filming_start: assignment.filming_start,
            filming_end: assignment.filming_end,
            event_date: eventDate,
            notes: assignment.notes || null,
            assignment_type:
              assignment.assignment_type || (assignment.artist_name ? "artist" : "other"),
            is_active: true,
            location: assignment.location || null,
            mirror_excluded: assignment.mirror_excluded ?? false,
            assist_crew_member_ids: assignment.assist_crew_member_ids ?? [],
          } as TablesInsert<"crew_assignments">;
        })
        .filter((a) => a !== null);

      // Save through the save-schedule-changes edge function. The RPC
      // (replace_crew_assignments) is service-role-only — going via the
      // edge function gives us a single audited entry point and keeps
      // the per-row push trigger off the hot path. The edge function
      // calls the RPC, the RPC computes the FK-aware diff and fires
      // ONE async pg_net.http_post → notify-schedule-changes for the
      // batched fan-out (no N+1, non-blocking).
      const { data: saveResult, error: rcaErr } = await supabase.functions.invoke<{
        rows: Array<{
          out_id: string;
          out_crew_member_id: string;
          out_artist_name: string | null;
          out_subject: string | null;
          out_stage_name: string;
          out_filming_start: string;
          out_filming_end: string;
          out_event_date: string;
          out_assignment_type: string;
        }>;
      }>("save-schedule-changes", {
        body: scheduleSaveBody(briefingId, assignmentsData, checkedScheduleRevision),
      });
      // Throws on a 409 (a phone edit landed after guardScheduleRevision's
      // check: schedule not written, shown once, this scope stays unsaved) and
      // on any other error (with the edge function's own message).
      await finishScheduleSave(
        scheduleRevisionRef.current,
        rcaErr,
        notifyScheduleStop,
        invokeErrorMessage,
      );
      type RcaRow = {
        out_id: string;
        out_crew_member_id: string;
        out_artist_name: string | null;
        out_subject: string | null;
        out_stage_name: string;
        out_filming_start: string;
        out_filming_end: string;
        out_event_date: string;
        out_assignment_type: string;
      };
      const rcaRows = (saveResult?.rows ?? []) as RcaRow[];
      const insertedAssignments = (rcaRows as RcaRow[]).map((r) => ({
        id: r.out_id,
        crew_member_id: r.out_crew_member_id,
        artist_name: r.out_artist_name,
        subject: r.out_subject,
        stage_name: r.out_stage_name,
        filming_start: r.out_filming_start,
        filming_end: r.out_filming_end,
        event_date: r.out_event_date,
      }));

      // Update React state with the post-apply ids. The
      // save-schedule-changes edge function returned the full active
      // crew_assignments set after the RPC's DEACTIVATE → INSERT →
      // UPDATE pass; for each local assignment without an id we find
      // a server row matching by natural key and adopt its id.
      // (The inline FE DEACTIVATE-first reorder from PR #98 is no
      // longer needed here — the RPC enforces that ordering server-
      // side.)
      if (insertedAssignments.length > 0) {
        const remaining = [...insertedAssignments];
        setCrewAssignments((prev) =>
          prev.map((assignment) => {
            if (assignment.id) return assignment;
            const match = remaining.find((inserted) => {
              if (inserted.crew_member_id !== assignment.crew_member_id) return false;
              if (inserted.event_date !== assignment.event_date) return false;
              if ((inserted.subject ?? null) !== (assignment.subject ?? null)) return false;
              if ((inserted.artist_name ?? null) !== (assignment.artist_name ?? null)) return false;
              if (inserted.stage_name !== assignment.stage_name) return false;
              return (
                normalizeTime(inserted.filming_start) === normalizeTime(assignment.filming_start) &&
                normalizeTime(inserted.filming_end) === normalizeTime(assignment.filming_end)
              );
            });
            if (match) {
              remaining.splice(remaining.indexOf(match), 1);
              return { ...assignment, id: match.id };
            }
            return assignment;
          }),
        );
      }
    }

    // Sync assignment filming_start/end to lineup set_start/end. The diff
    // above only catches changes that propagate through React state;
    // editing a lineup slot's time doesn't touch crewAssignments state,
    // so without this an assignment would remain pointing at the old slot
    // even though the lineup view shows the artist at the new time.
    if (runLineup && timetable.length > 0) {
      // Select lineup_id so the FK fast-path in
      // computeAssignmentTimeUpdates engages — without it the helper
      // falls back to text-keyed (artist_name + stage_name) matching,
      // which breaks on stage rename: prevByArtist lookup using the
      // new stage_name misses the snapshot's old-named row, the
      // "preserve manual resize" check is skipped, and the assignment's
      // filming gets reset to the slot's full length.
      const { data: freshAssignments } = await supabase
        .from("crew_assignments")
        .select("id, lineup_id, artist_name, stage_name, event_date, filming_start, filming_end")
        .eq("briefing_id", briefingId)
        .eq("is_active", true);

      const timeUpdates = computeAssignmentTimeUpdates(
        // Pass `id` so newByLineupId is built — same FK-survives-
        // rename argument as freshAssignments.lineup_id above.
        timetable.map((t) => ({
          id: t.id,
          artist_name: t.artist_name,
          stage_name: resolveStage(t.stage_name),
          event_date: t.event_date,
          set_start: t.set_start,
          set_end: t.set_end,
        })),
        freshAssignments ?? [],
        // The snapshot was taken at the top of handleSave — before
        // briefing_stages.label updates fired. Map old stage_name
        // through the rename map so the prev-vs-new comparison runs
        // in a consistent label space. Belt-and-braces alongside the
        // FK fast-path: covers legacy assignments whose lineup_id is
        // still NULL pre-backfill.
        previousLineupSnapshot.map((row) => ({
          ...row,
          stage_name: resolveStage(row.stage_name),
        })),
      );

      if (timeUpdates.length > 0) {
        // Split outcomes into write-paths.
        const deactivations = timeUpdates.filter(
          (u): u is { id: string; deactivate: true } => "deactivate" in u && u.deactivate === true,
        );
        const writes = timeUpdates.filter(
          (
            u,
          ): u is {
            id: string;
            artist_name?: string;
            filming_start: string;
            filming_end: string;
          } => !("deactivate" in u),
        );
        // One batched RPC handles every patch + every deactivation in
        // a single transaction. Replaces the previous Promise.all of
        // N parallel `.update().eq("id", ...)` calls — each one was a
        // separate PostgREST transaction, WAL record, and Realtime
        // broadcast. The RPC is diff-guarded so no-op patches at the
        // tail of a producer save fall out without any UPDATEs.
        await scheduleWriteAtRevision(scheduleRevisionRef.current, notifyScheduleStop, () =>
          applyAssignmentTimePatches(
            supabase,
            briefingId,
            writes.map((u) => ({
              id: u.id,
              filming_start: u.filming_start,
              filming_end: u.filming_end,
              ...(u.artist_name !== undefined ? { artist_name: u.artist_name } : {}),
            })),
            deactivations.map((d) => d.id),
            checkedScheduleRevision,
          ),
        );

        const deactivatedIds = new Set(deactivations.map((d) => d.id));
        setCrewAssignments((prev) =>
          prev
            .filter((a) => !deactivatedIds.has(a.id ?? ""))
            .map((a) => {
              const u = writes.find((x) => x.id === a.id);
              if (!u) return a;
              return {
                ...a,
                filming_start: u.filming_start,
                filming_end: u.filming_end,
                ...(u.artist_name !== undefined ? { artist_name: u.artist_name } : {}),
              };
            }),
        );

        // Note: producers are warned BEFORE a swap-by-arrow via the
        // confirm dialog driven by pendingMoveSwap. Here we silently
        // apply the post-save sync — no toast, no banner. Other
        // lineup edits (manual time tweaks, AI re-extract) flow
        // through this path too, but their auto-rebind is implicit
        // and matches the schedule-step preview.
      }
    }

    // Update original labels so subsequent saves don't re-apply the same renames
    if (runStages) {
      setOriginalStageLabels(new Map(stages.map((s) => [s.name, s.label])));
    }

    // Per-day doors: upsert briefing_day_doors from the local
    // festivalDays state. The producer UI already tracks per-day
    // startTime/endTime; this wiring makes the per-day overrides
    // persist (previously the save flattened to first-day on
    // briefings.doors_open/close). briefings.doors_open/close stays
    // as the briefing-level fallback for legacy consumers — see
    // doorsForDay helper for the resolve rule.
    //
    // Gated on the "days" scope (step 0, where the Festival Days cards
    // live), not on "lineup": the producer types these times on Basic
    // information and may never open the timetable step at all.
    //
    // Never write days we never managed to read. festivalDays starts as
    // ONE placeholder day (today, 12:00-23:00); the real days arrive
    // from the hydration effect, which needs the doors read to resolve.
    // If that read errors, React Query leaves it undefined and hydration
    // never runs — while festivalName and location DO hydrate off the
    // briefing row, so step-0 validation passes and the form looks
    // usable. Writing the placeholder then puts every real festival date
    // in toDelete and wipes the producer's per-day windows. A backend
    // hiccup must not become data loss.
    const daysAreTrustworthy = hasInitializedDays || locallyCreatedRef.current === briefingId;
    if (runDays && daysAreTrustworthy) {
      await saveDayDoors(supabase, briefingId, festivalDays);
    }

    // Notification dispatch is owned entirely by the backend: triggers
    // wrote rows into pending_push_notifications, and a deferred
    // constraint trigger on that table fires once at the COMMIT of
    // this very transaction to bundle + send (see migration
    // 20260429170000). No frontend RPC, no abuse surface.

    // Mark the slices we just persisted as "clean" so the next
    // Next/Back click in the same scope short-circuits without a
    // DB round-trip. Only the slices owned by this scope move; the
    // others keep their previous value so an "all" save catches
    // changes the producer made on still-clean steps.
    savedSnapshotsRef.current = captureSavedSnapshot(
      scope,
      currentSnapshot,
      savedSnapshotsRef.current,
    );
  };

  // Save live tracking data (map settings and festival areas)
  const saveLiveTrackingData = async (briefingId: string) => {
    if (!briefingId) return;

    try {
      // Save map settings
      if (mapCenter && mapZoom) {
        await supabase.from("map_settings").upsert(
          {
            briefing_id: briefingId,
            center_latitude: mapCenter[1],
            center_longitude: mapCenter[0],
            zoom_level: mapZoom,
            is_locked: isMapLocked,
          },
          {
            onConflict: "briefing_id",
          },
        );

        // Map settings save attempted
      }

      // Festival boundary and stage zones are managed directly by LiveTrackingSetup
      // (saveBoundaryToDB / saveStageZoneToDB). Do NOT re-save here — the parent state
      // may be stale and would overwrite correctly-saved DB data.
    } catch (error) {
      console.error(error);
    }
  };

  // Send bundled notification for first publication
  const sendPublishNotification = async (briefingId: string, productionName: string) => {
    try {
      // Get crew members with user_ids and their assignments
      const { data: crewMembers, error: crewError } = await supabase
        .from("crew_members")
        .select("id, user_id, name")
        .eq("briefing_id", briefingId)
        .not("user_id", "is", null);

      if (crewError || !crewMembers?.length) {
        return;
      }

      const { data: assignments } = await supabase
        .from("crew_assignments")
        .select("crew_member_id")
        .eq("briefing_id", briefingId)
        .eq("is_active", true);

      // Group assignments by user
      const uniqueUsers = [...new Map(crewMembers.map((c) => [c.user_id, c])).values()];

      for (const crewMember of uniqueUsers) {
        const userCrewIds = crewMembers
          .filter((c) => c.user_id === crewMember.user_id)
          .map((c) => c.id);

        const userAssignmentCount =
          assignments?.filter((a) => a.crew_member_id && userCrewIds.includes(a.crew_member_id))
            .length || 0;

        const body =
          userAssignmentCount > 0
            ? `You have ${userAssignmentCount} assignment(s). Open the app to view your schedule.`
            : `You've been added to this production. Open the app for details.`;

        try {
          await supabase.functions.invoke("notify", {
            body: {
              userId: crewMember.user_id,
              // notify only lets a signed-in user push to crew of a
              // production they can edit, and needs to know which one.
              briefingId,
              title: `${productionName} - You're assigned!`,
              body,
              url: `/crew/briefing/${briefingId}`,
              tag: `production-published-${briefingId}`,
              notificationType: "new_assignment",
            },
          });
        } catch (notifyError) {
          console.error(notifyError);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Main save function. The `scope` argument is forwarded to
  // saveRelatedData so step Next/Back can save only its own tables —
  // see SaveScope. Callers that haven't been migrated (or that are
  // explicitly "save everything", like Publish) get "all".
  // One Camera ID slot per (camera/photo person, day). A person with
  // several camera functions on a day collapses to a single slot; their
  // functions are listed for context. Rows are ordered by each person's
  // first appearance in `crew` — the SAME order the crew step renders its
  // per-person cards in — so the list (and the per-day auto-assign that
  // walks it) matches the order crew were entered earlier.
  const cameraIdRows = useMemo<CameraIdRow[]>(() => {
    // Single pass: assign each person an order index on first appearance
    // in `crew` (mirrors the crew step's first-seen grouping) and collapse
    // camera/photo rows into one slot per (person, day).
    const personOrder = new Map<string, number>();
    const byKey = new Map<string, CameraIdRow & { _order: number }>();
    for (const m of crew) {
      const personKey = cameraIdPersonKey(m);
      if (!personKey) continue;
      if (!personOrder.has(personKey)) personOrder.set(personKey, personOrder.size);
      if (!isCameraIdRole(m.function)) continue;
      const key = `${personKey}__${m.event_date}`;
      const existing = byKey.get(key);
      if (existing) {
        if (m.function && !existing.functions.includes(m.function)) {
          existing.functions.push(m.function);
        }
        if (!existing.cameraId && m.camera_id) existing.cameraId = m.camera_id;
      } else {
        byKey.set(key, {
          key,
          name: m.name,
          functions: m.function ? [m.function] : [],
          eventDate: m.event_date,
          cameraId: m.camera_id || "",
          _order: personOrder.get(personKey)!,
        });
      }
    }
    return Array.from(byKey.values())
      .sort((a, b) => a._order - b._order || a.eventDate.localeCompare(b.eventDate))
      .map(({ _order, ...row }) => row);
  }, [crew]);

  // Write a Camera ID onto every crew row for the same (person, day) so
  // all of that person's day rows stay consistent regardless of function.
  const handleCameraIdChange = useCallback((key: string, cameraId: string) => {
    setCrew((prev) =>
      prev.map((m) =>
        `${cameraIdPersonKey(m)}__${m.event_date}` === key ? { ...m, camera_id: cameraId } : m,
      ),
    );
  }, []);

  const handleSave = async (
    status: "draft" | "published" = "draft",
    _trackBriefingChanges: boolean = false,
    scope: SaveScope = "all",
  ) => {
    try {
      if (!userId) {
        toast({
          title: "Sessie verlopen",
          description: "Je sessie is verlopen. Log opnieuw in.",
          variant: "destructive",
        });
        navigate("/producer-login");
        return;
      }

      // Camera IDs must be unique per day. Abort with a clear message
      // rather than letting duplicates through (no hard DB constraint —
      // see migration 20260603120000_camera_id_per_day).
      const conflictDay = firstCameraIdConflictDate(crew);
      if (conflictDay) {
        toast({
          title: "Dubbele Camera ID",
          description: `Op ${format(new Date(conflictDay), "d MMM yyyy")} hebben meerdere crewleden dezelfde Camera ID. Camera ID's moeten per dag uniek zijn.`,
          variant: "destructive",
        });
        return;
      }

      // Before the FIRST write below (map upload, briefing row, profile,
      // related data). A phone edit since this editor read the schedule
      // throws ScheduleChangedElsewhereError (already shown) — nothing saved.
      const checkedScheduleRevision = await guardScheduleRevision(scope);

      // Detect if this is a FIRST publish (status changes from draft to published)
      const isFirstPublish = status === "published" && existingBriefing?.status !== "published";

      // Prevent published productions from being saved as draft
      if (existingBriefing?.status === "published" && status === "draft") {
        status = "published";
      }

      // If the producer queued a new map file this step, upload it
      // through the upload-briefing-map edge function. Returns the
      // storage object key we'll persist to briefings.map_storage_path.
      // Requires a briefing id — for the very first save (no
      // effectiveId yet) we defer the upload to the next save.
      let finalMapStoragePath: string | null = mapStoragePath;
      if (mapFile && effectiveId) {
        finalMapStoragePath = await uploadFestivalMap(effectiveId, mapFile);
        setMapStoragePath(finalMapStoragePath);
        setMapFile(null);
      }

      const briefingData = {
        ...getBriefingData(),
        map_storage_path: finalMapStoragePath,
      };

      // Snapshot the persisted briefings.stages JSONB BEFORE
      // saveProduction overwrites it with the producer's renamed
      // labels. saveRelatedData uses this as the rename-map
      // baseline so the propagation is immune to React-state
      // staleness (originalStageLabels can be empty during the
      // initial render window or get overwritten by an
      // existingBriefing refetch fired by saveProduction's own
      // queryClient.invalidateQueries). When there's no existing
      // briefing yet (first save of a new production) we have
      // nothing to compare against, so the snapshot stays empty
      // — saveRelatedData treats that as "no renames possible".
      // Latch before the insert: createProduction publishes the new id
      // via setProductionId the instant the INSERT returns, which starts
      // the ["briefing_day_doors", newId] read. That read resolves EMPTY
      // — the doors write below hasn't landed yet — and the hydration
      // effect would stamp day 1's window over every day the producer
      // just typed. The rows in the database stay correct; the form the
      // producer clicks Previous back to does not. See the ref's
      // declaration for why it isn't state.
      if (!currentBriefingId) {
        locallyCreatedRef.current = "pending";
      }
      // Distinguishes "this click inserts a production" from "this click
      // updates one this editor created a moment ago" — saveProduction
      // routes on the hook's productionId, not on the URL, so a retry
      // after a failed first Next is an UPDATE even though the URL is
      // still /briefings/create. Only a real insert lets us claim the
      // stored doors are empty.
      const isInsertingProduction = !currentBriefingId && !productionId;

      let baselineStagesSnapshot: Array<{ name: string; label: string }> | undefined = undefined;
      if (currentBriefingId) {
        const { data: snap } = await supabase
          .from("briefings")
          .select("stages")
          .eq("id", currentBriefingId)
          .single();
        if (snap?.stages && Array.isArray(snap.stages)) {
          baselineStagesSnapshot = (parseStages(snap.stages) as Stage[]).map((s) => ({
            name: s.name,
            label: s.label,
          }));
        }
      }

      // For FIRST PUBLISH: save related data FIRST while status is still draft
      // This prevents the track_assignment_changes trigger from sending individual notifications
      if (isFirstPublish) {
        // Save briefing as draft first to get the ID
        const draftResult = await saveProduction(briefingData, "draft");
        const finalBriefingId = currentBriefingId || draftResult?.id;

        if (!finalBriefingId) {
          throw new Error("Failed to get briefing ID");
        }

        // Save producer information
        if (producerPhone || invoiceInformation) {
          await supabase
            .from("profiles")
            .update({
              phone: producerPhone || null,
              invoice_information: invoiceInformation || null,
            })
            .eq("id", userId!);
        }

        // Save related data (assignments, lineup, crew) WHILE STILL DRAFT
        await saveRelatedData(baselineStagesSnapshot, scope, checkedScheduleRevision);
        await saveLiveTrackingData(finalBriefingId);

        // NOW change status to published (no assignment notifications will fire since they're already saved)
        const { data: publishedBriefing } = await supabase
          .from("briefings")
          .update({ status: "published", published_at: new Date().toISOString() })
          .eq("id", finalBriefingId)
          .select()
          .single();

        // Send ONE bundled notification per crew member
        await sendPublishNotification(finalBriefingId, festivalName || "New Production");

        // Pass the freshly-updated row so the helper writes it directly
        // into the briefing/briefing-view caches — saves the refetches
        // those queries would otherwise trigger on the editor and on
        // BriefingView mount after navigate.
        await invalidateBriefingCaches(queryClient, finalBriefingId, {
          briefing: publishedBriefing ?? draftResult,
        });

        toast({
          title: "Briefing Published",
          description: "You can now invite your crew.",
        });
        clearProductionState();
        navigate(`/productions/${finalBriefingId}/crew`);

        return draftResult;
      }

      // Regular save flow (draft saves or updates to already-published productions)
      const result = await saveProduction(briefingData, status);

      // For editing existing briefings, use URL ID; for new briefings, use result ID
      const finalBriefingId = currentBriefingId || result?.id;

      // If this is a new briefing (no URL ID), navigate to edit route with new ID
      // Pin the hydration guard to the production it was raised for.
      // Anything still "pending" is released in the finally below, so no
      // path can leave hydration blocked for the rest of the mount.
      if (locallyCreatedRef.current === "pending" && !currentBriefingId && result?.id) {
        locallyCreatedRef.current = result.id;
      }

      if (!currentBriefingId && result?.id) {
        // Persist the per-day doors BEFORE navigating. saveRelatedData
        // is never reached on this path (we return right below), so
        // without this write briefing_day_doors stays empty and every
        // reader falls back to briefings.doors_open/close — day 1's
        // window stamped onto the whole festival, which is what the
        // crew-schedule timeline then anchors on. The baseline is
        // known-empty: this briefing was inserted milliseconds ago, so
        // pass it rather than paying a round-trip to rediscover it.
        const savedDayDoors = await saveDayDoors(
          supabase,
          result.id,
          festivalDays,
          isInsertingProduction ? [] : undefined,
        );
        // The doors read for this brand-new id already resolved empty
        // (see the latch at the top of this function). Replace that
        // cached answer with what we just wrote — the editor keeps this
        // cache across the navigate below, and hands it to the review
        // step, so leaving the empty answer in place would render the
        // festival as if every day shared day 1's window.
        // Cancel first: setQueryData does not stop the read that is
        // already in flight, and when that empty answer settles it
        // overwrites the seed — the same flattened festival, just
        // non-deterministically and only on slower connections.
        await queryClient.cancelQueries({ queryKey: briefingDayDoorsQuery(result.id).queryKey });
        queryClient.setQueryData(briefingDayDoorsQuery(result.id).queryKey, savedDayDoors);
        // Same reason the hydration effect seeds this: the rows we just
        // wrote ARE the saved state, so the next step-0 navigation must
        // not re-upsert them.
        savedSnapshotsRef.current = {
          ...savedSnapshotsRef.current,
          festivalDays: JSON.stringify(festivalDays),
        };
        navigate(`/briefings/${result.id}/edit?step=${currentStep + 1}`, { replace: true });
        return result;
      }

      // NOTE: Briefing change tracking is handled by database trigger (track_briefing_changes_clean)
      // Removed frontend tracking to prevent double notifications

      if (finalBriefingId) {
        // Save producer information to profiles table
        if (producerPhone || invoiceInformation) {
          await supabase
            .from("profiles")
            .update({
              phone: producerPhone || null,
              invoice_information: invoiceInformation || null,
            })
            .eq("id", userId!);
        }

        await saveRelatedData(baselineStagesSnapshot, scope, checkedScheduleRevision);
        await saveLiveTrackingData(finalBriefingId);

        // saveProduction's result is the freshly UPDATEd briefings row.
        // Pass it as knownData so the helper writes it into the cache
        // instead of triggering refetches. Also pass the post-apply
        // briefing_stages snapshot from saveRelatedData so the editor's
        // useEffect synthesis runs against fresh data on the very next
        // render — no flicker through stale-cache state.
        await invalidateBriefingCaches(queryClient, finalBriefingId, {
          briefing: result,
          briefingStages: latestBriefingStagesSnapshot.current ?? undefined,
        });

        if (status === "published") {
          toast({
            title: "Changes Saved",
            description: "Your production has been updated.",
          });
        } else {
          toast({
            title: "Draft saved",
            description: "Your changes have been saved.",
          });
        }
      }

      return result;
    } catch (error: unknown) {
      console.error(error);
      // guardScheduleRevision / saveRelatedData already told the producer
      // (sonner) to reload.
      if (!(error instanceof ScheduleChangedElsewhereError)) {
        const message = error instanceof Error ? error.message : "Failed to save your changes.";
        toast({
          title: "Error saving briefing",
          description: message,
          variant: "destructive",
        });
      }
      throw error;
    } finally {
      // Still "pending" means no production was created on this click —
      // it failed, or this was the publish path. Either way the form has
      // no claim to be the source of truth, and leaving the latch up
      // would block hydration for the rest of this mount.
      if (locallyCreatedRef.current === "pending") {
        locallyCreatedRef.current = null;
      }
    }
  };

  // Default arrival time = doors_open - 1 hour, clamped, with a sane
  // 12:00 fallback for festival days that haven't set a start time yet.
  // Used by the day-checkbox flow when a producer toggles a day ON.
  const getDefaultArrivalForDay = (dayDate: string): string => {
    const day = festivalDays.find((d) => d.date === dayDate);
    if (!day?.startTime) return "12:00";
    const [h, m] = day.startTime.split(":").map(Number);
    const hh = String(Math.max(0, (h ?? 0) - 1)).padStart(2, "0");
    const mm = String(m ?? 0).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Group key for the per-person card. Empty email = its own group
  // keyed by row position so half-typed rows don't collide.
  const crewGroupKeyOf = (member: CrewMember, idx: number): string => {
    if (member._personId) return `__pid-${member._personId}__`;
    const e = (member.email ?? "").trim().toLowerCase();
    return e || `__blank-${idx}__`;
  };

  // Mint a stable client-side person id. Used by addPersonAcrossDays
  // and handleToggleDayForPerson(on=true) so a freshly-added card
  // never loses its group identity while the producer is typing the
  // email.
  const mintPersonId = (): string =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `pid-${Math.random().toString(36).slice(2)}`;

  // Add a new crew member entry. Inserts ONE row per festival day with
  // the same person fields (name, email, phone, function, color) so the
  // card opens with all days checked. Auto-expands + auto-edits the new
  // card. Same crew-limit check as the legacy single-day add — only
  // triggers when at least one day would exceed the plan's per-day cap.
  const addPersonAcrossDays = async () => {
    if (festivalDays.length === 0) {
      toast({
        title: "Add a festival day first",
        description: "Crew members are added across the briefing's days.",
      });
      return;
    }

    if (currentBriefingId && session) {
      // Check the busiest existing day; if that day + 1 fits the cap,
      // every other day fits too.
      const maxDayCount = Math.max(
        0,
        ...festivalDays.map((d) => crew.filter((c) => c.event_date === d.date).length),
      );
      try {
        const { data: limitCheck, error } = await supabase.functions.invoke("check-crew-limit", {
          body: { briefingId: currentBriefingId, currentCrewCount: maxDayCount },
        });
        if (error) {
          console.error(error);
        } else if (limitCheck && !limitCheck.can_add) {
          let upgradeTo = "PRO";
          let description = limitCheck.reason;
          if (limitCheck.plan_tier === "lite") {
            upgradeTo = "PRO";
            description =
              "Your LITE plan allows only 1 crew member (yourself). Upgrade to PRO to add up to 10 crew members per production.";
          } else if (limitCheck.plan_tier === "pro") {
            upgradeTo = "STUDIO";
            description =
              "Your PRO plan allows a maximum of 10 crew members per production. Upgrade to STUDIO for unlimited crew.";
          }
          toast({
            title: "Crew limit reached",
            description,
            variant: "destructive",
            action: (
              <Button variant="outline" size="sm" onClick={() => navigate("/upgrade")}>
                Upgrade to {upgradeTo}
              </Button>
            ),
            duration: 8000,
          });
          return;
        }
      } catch (error) {
        console.error(error);
      }
    }

    const colorIndex = crew.filter((c) => c.hasColor).length % DEFAULT_COLORS.length;
    const isLiteTier = subscriptionStatus?.plan_tier === "lite";
    const isFirstCrewMember = crew.length === 0;
    const prefillName =
      isLiteTier && isFirstCrewMember && producerProfile?.full_name
        ? producerProfile.full_name
        : "";
    const prefillEmail = isLiteTier && isFirstCrewMember && user?.email ? user.email : "";

    // Single client-side person id shared by every day-row of this
    // new card. Survives email edits — the editor's group key is
    // `__pid-${_personId}__`, so typing the email never re-keys the
    // card and the Collapsible never remounts mid-keystroke.
    const personId = mintPersonId();
    const newRows: CrewMember[] = festivalDays.map((day) => ({
      _personId: personId,
      name: prefillName,
      email: prefillEmail,
      phone: "",
      function: "",
      arrival_time: getDefaultArrivalForDay(day.date),
      event_date: day.date,
      color: DEFAULT_COLORS[colorIndex],
      hasColor: true,
    }));
    setCrew([...crew, ...newRows]);

    const newKey = `__pid-${personId}__`;
    setExpandedCrewIds((prev) => new Set([...prev, newKey]));
    if (isPublished) {
      setEditingCrewIds((prev) => new Set([...prev, newKey]));
    }
  };

  // Toggle a crew member's presence on a specific festival day.
  // ON: insert a new row inheriting the group's person fields with a
  // sane default arrival_time. OFF: remove the row, but if it has any
  // crew_assignments first surface the cascade-delete count via the
  // pendingCrewRemoval AlertDialog so the producer confirms.
  const handleToggleDayForPerson = (
    group: {
      personId?: string;
      email: string;
      name: string;
      phone: string | null;
      function: string;
      color: string | null;
      hasColor: boolean;
      rowsByDay: Map<string, CrewMember>;
      rowIndicesByDay: Map<string, number>;
    },
    day: { date: string },
    on: boolean,
  ) => {
    if (on) {
      // Inherit the group's _personId so the new day-row stays in the
      // same person card. Without this, the new row would be its own
      // group (keyed by lower(email)) — fine when the email is set,
      // but broken when the producer is mid-add and the email is
      // still blank.
      const newRow: CrewMember = {
        _personId: group.personId,
        name: group.name,
        email: group.email,
        phone: group.phone ?? "",
        function: group.function,
        arrival_time: getDefaultArrivalForDay(day.date),
        event_date: day.date,
        color: group.color ?? undefined,
        hasColor: group.hasColor,
      };
      setCrew([...crew, newRow]);
      return;
    }
    const idx = group.rowIndicesByDay.get(day.date);
    if (idx === undefined) return;
    const row = crew[idx];
    const assignmentCount = row?.id
      ? crewAssignments.filter((a) => a.crew_member_id === row.id).length
      : 0;
    const remove = () => {
      setCrew(crew.filter((_, i) => i !== idx));
    };
    if (assignmentCount === 0) {
      remove();
      return;
    }
    setPendingCrewRemoval({
      kind: "day",
      label: `${group.name || group.email || "this person"} on ${day.date}`,
      assignmentCount,
      confirm: remove,
    });
  };

  // Remove an entire person across all their festival days. Sums the
  // assignment counts across every day's crew_members.id and warns
  // before the cascade-delete lands.
  const handleRemovePerson = (group: {
    key: string;
    email: string;
    name: string;
    rowsByDay: Map<string, CrewMember>;
  }) => {
    const ids = new Set(
      Array.from(group.rowsByDay.values())
        .map((r) => r.id)
        .filter((id): id is string => Boolean(id)),
    );
    const assignmentCount = crewAssignments.filter(
      (a) => a.crew_member_id && ids.has(a.crew_member_id),
    ).length;
    const groupKey = group.key;
    const remove = () => {
      // Match by the same group key the renderer uses (crewGroupKeyOf)
      // — robust against blank-email half-typed rows (each gets its own
      // `__blank-${idx}__` key) and against a producer renaming the
      // person's email between mount and click.
      setCrew(crew.filter((c, i) => crewGroupKeyOf(c, i) !== groupKey));
      setExpandedCrewIds((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
      setEditingCrewIds((prev) => {
        const next = new Set(prev);
        next.delete(groupKey);
        return next;
      });
    };
    if (assignmentCount === 0) {
      remove();
      return;
    }
    setPendingCrewRemoval({
      kind: "person",
      label: group.name || group.email || "this crew member",
      assignmentCount,
      confirm: remove,
    });
  };

  const addFestivalDay = () => {
    const lastDay = festivalDays[festivalDays.length - 1];
    const nextDate = new Date(lastDay.date);
    nextDate.setDate(nextDate.getDate() + 1);

    setFestivalDays([
      ...festivalDays,
      {
        date: nextDate.toISOString().split("T")[0],
        startTime: lastDay.startTime,
        endTime: lastDay.endTime,
      },
    ]);
  };
  const removeFestivalDay = (index: number) => {
    if (festivalDays.length > 1) {
      setFestivalDays(festivalDays.filter((_, i) => i !== index));

      // Remove stages for the deleted day and re-index stages for subsequent days
      const updatedStages = stages
        .filter((s) => !s.name.startsWith(`day${index}_`))
        .map((s) => {
          const match = s.name.match(/^day(\d+)_(.+)$/);
          if (match) {
            const dayNum = parseInt(match[1]);
            if (dayNum > index) {
              return { ...s, name: `day${dayNum - 1}_${match[2]}` };
            }
          }
          return s;
        });
      setStages(updatedStages);
    }
  };

  // Track original date when user starts editing
  const [editingDateOriginal, setEditingDateOriginal] = useState<{
    dayIndex: number;
    date: string;
  } | null>(null);

  // Handle date input change (visual update only)
  const handleDateInputChange = (dayIndex: number, newDate: string) => {
    const newDays = [...festivalDays];
    newDays[dayIndex].date = newDate;
    setFestivalDays(newDays);
  };

  // Handle date focus - store original value
  const handleDateFocus = (dayIndex: number) => {
    setEditingDateOriginal({ dayIndex, date: festivalDays[dayIndex].date });
  };

  // Handle date blur - validate and show warning if needed
  const handleDateBlur = async (dayIndex: number) => {
    if (!editingDateOriginal || editingDateOriginal.dayIndex !== dayIndex) return;

    const oldDate = editingDateOriginal.date;
    const newDate = festivalDays[dayIndex].date;

    // Clear the editing state
    setEditingDateOriginal(null);

    // If date didn't change, just return
    if (oldDate === newDate) return;

    // Validate that newDate is a valid date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!newDate || newDate.length !== 10 || !dateRegex.test(newDate)) {
      // Revert to original date if invalid
      const newDays = [...festivalDays];
      newDays[dayIndex].date = oldDate;
      setFestivalDays(newDays);
      return;
    }

    // Check if there's existing data for the OLD date
    const lineupForDate = timetable.filter((t) => t.event_date === oldDate);
    const crewForDate = crew.filter((c) => c.event_date === oldDate);
    const assignmentsForDate = crewAssignments.filter((a) => a.event_date === oldDate);

    const hasExistingData =
      lineupForDate.length > 0 || crewForDate.length > 0 || assignmentsForDate.length > 0;

    if (hasExistingData) {
      // Revert to old date first (will be updated if user confirms shift)
      const newDays = [...festivalDays];
      newDays[dayIndex].date = oldDate;
      setFestivalDays(newDays);

      // Show warning dialog
      setDateChangeDialogOpen(true);
    }
    // If no existing data, the date is already updated via handleDateInputChange
  };

  // Validation function for each step
  const validateStep = async (
    step: number,
  ): Promise<{ valid: boolean; message?: string; warning?: boolean }> => {
    switch (step) {
      case 0: // Basic Information
        if (!festivalName.trim()) {
          return { valid: false, message: "Festival name is required" };
        }
        if (!location.trim()) {
          return { valid: false, message: "Location is required" };
        }
        return { valid: true };

      case 1: {
        // Stages per Day — optional. A planning can be built with custom
        // subjects only, so no stage is required on any day.
        return { valid: true };
      }

      case 2: // Timetable - check for overlapping times
        // Check each stage/date combination for overlapping times
        for (const day of festivalDays) {
          const dayTimetable = timetable.filter((entry) => entry.event_date === day.date);
          const stageGroups = dayTimetable.reduce(
            (acc, entry) => {
              if (!acc[entry.stage_name]) acc[entry.stage_name] = [];
              acc[entry.stage_name].push(entry);
              return acc;
            },
            {} as Record<string, typeof timetable>,
          );

          for (const [stageName, entries] of Object.entries(stageGroups)) {
            // Check each pair of entries for overlap
            for (let i = 0; i < entries.length; i++) {
              for (let j = i + 1; j < entries.length; j++) {
                const a = entries[i];
                const b = entries[j];

                // Convert times to minutes for comparison
                const aStart =
                  parseInt(a.set_start.split(":")[0]) * 60 + parseInt(a.set_start.split(":")[1]);
                const aEnd =
                  parseInt(a.set_end.split(":")[0]) * 60 + parseInt(a.set_end.split(":")[1]);
                const bStart =
                  parseInt(b.set_start.split(":")[0]) * 60 + parseInt(b.set_start.split(":")[1]);
                const bEnd =
                  parseInt(b.set_end.split(":")[0]) * 60 + parseInt(b.set_end.split(":")[1]);

                // Check for overlap
                if (aStart < bEnd && aEnd > bStart) {
                  const dayStr = format(new Date(day.date), "MMMM d");
                  return {
                    valid: false,
                    message: `Time overlap detected on ${dayStr} at ${stageName}: "${a.artist_name}" (${formatTime(a.set_start)}-${formatTime(a.set_end)}) overlaps with "${b.artist_name}" (${formatTime(b.set_start)}-${formatTime(b.set_end)})`,
                  };
                }
              }
            }
          }
        }
        // Warning if no lineup at all
        if (timetable.length === 0) {
          return {
            valid: false,
            warning: true,
            message:
              "No lineup has been entered. Without a lineup, no crew schedule can be generated with AI. Are you sure you want to continue?",
          };
        }
        // Warning if not all stages have artists. FK-keyed: bridge
        // each editor stage to its briefing_stages.id and key the
        // "filled" set on stage_id when both sides have it. Producer
        // rename mid-edit no longer false-positives this warning.
        {
          const stageIdByLabelLocal = new Map(existingBriefingStages.map((s) => [s.label, s.id]));
          const allStages = festivalDays.flatMap((day, dayIndex) => {
            const dayStages = wizardStagesForDay(stages, dayIndex);
            return dayStages.map((s) => {
              const fkId = stageIdByLabelLocal.get(s.label) ?? null;
              return {
                key: `${day.date}::${fkId ? `id:${fkId}` : `name:${s.label}`}`,
                label: s.label,
              };
            });
          });
          const filledStages = new Set(
            timetable.map((entry) => {
              const entryStageId =
                (entry as { stage_id?: string | null }).stage_id ??
                stageIdByLabelLocal.get(entry.stage_name) ??
                null;
              return `${entry.event_date}::${entryStageId ? `id:${entryStageId}` : `name:${entry.stage_name}`}`;
            }),
          );
          const emptyStages = allStages.filter((s) => !filledStages.has(s.key));
          if (emptyStages.length > 0) {
            const stageNames = [...new Set(emptyStages.map((s) => s.label))].join(", ");
            return {
              valid: false,
              warning: true,
              message: `Not all stages have been filled in (${stageNames}). Are you sure you want to continue?`,
            };
          }
        }
        return { valid: true };

      case 3: {
        // The Crew
        if (crew.length === 0) {
          return { valid: false, message: "All crew members must have a name and email address" };
        }
        const invalidCrew = crew.some((member) => !member.name.trim() || !member.email.trim());
        if (invalidCrew) {
          return { valid: false, message: "All crew members must have a name and email address" };
        }

        // Check if all festival days have at least one crew member
        const daysWithoutCrew = festivalDays.filter((day) => {
          const dayCrewMembers = crew.filter((m) => m.event_date === day.date);
          return dayCrewMembers.length === 0;
        });

        if (daysWithoutCrew.length > 0) {
          const dayNames = daysWithoutCrew
            .map((d) => format(new Date(d.date), "MMMM d"))
            .join(", ");
          return {
            valid: false,
            message: `Missing crew members for the following day(s): ${dayNames}. Please add at least one crew member for each festival day.`,
          };
        }

        // Check for duplicate emails on the same day
        for (const day of festivalDays) {
          const dayCrewMembers = crew.filter((m) => m.event_date === day.date);
          const emails = dayCrewMembers.map((m) => m.email.toLowerCase());
          const uniqueEmails = new Set(emails);
          if (emails.length !== uniqueEmails.size) {
            return {
              valid: false,
              message:
                "Duplicate email addresses found on the same day. Please use unique emails for each crew member.",
            };
          }
        }

        return { valid: true };
      }

      case 5: {
        // Crew Schedules - check if all activated artists are assigned
        // Get all activated artists from timetable
        const activatedArtists = timetable.filter(
          (entry) => entry.artist_name && entry.artist_name.trim() !== "",
        );

        // Check if each activated artist has at least one assignment.
        // FK-first via lineup_id (post-migration 20260504240200) so a
        // producer rename mid-edit doesn't false-positive every
        // artist as "unassigned"; text-tuple fallback for legacy
        // rows that haven't been backfilled.
        const unassignedArtists = activatedArtists.filter((artist) => {
          const artistId = (artist as { id?: string | null }).id ?? null;
          return !crewAssignments.some((assignment) => {
            if (assignment.assignment_type !== "artist") return false;
            const aLineupId = (assignment as { lineup_id?: string | null }).lineup_id ?? null;
            if (aLineupId && artistId) return aLineupId === artistId;
            return (
              assignment.artist_name === artist.artist_name &&
              assignment.stage_name === artist.stage_name
            );
          });
        });

        if (unassignedArtists.length > 0) {
          // Group unassigned artists by day and stage for better clarity
          const unassignedByDay = unassignedArtists.reduce(
            (acc, artist) => {
              const key = artist.event_date;
              if (!acc[key]) acc[key] = {};
              if (!acc[key][artist.stage_name]) acc[key][artist.stage_name] = [];
              acc[key][artist.stage_name].push(artist.artist_name);
              return acc;
            },
            {} as Record<string, Record<string, string[]>>,
          );

          const detailedMessage = Object.entries(unassignedByDay)
            .map(([date, stages]) => {
              const dayStr = format(new Date(date), "MMMM d");
              const stageStrs = Object.entries(stages)
                .map(([stage, artists]) => `${stage}: ${artists.join(", ")}`)
                .join(" | ");
              return `${dayStr} - ${stageStrs}`;
            })
            .join("\n");

          return {
            valid: false,
            message: `Not all artists are assigned. Missing assignments:\n${detailedMessage}`,
            warning: true, // This is a warning, not a hard error
          };
        }
        return { valid: true };
      }

      case 6: // General Briefing
        if (!navigationAddress.trim()) {
          return { valid: false, message: "Navigation address is required" };
        }
        return { valid: true };

      case 7: {
        // Live Tracking Setup - require all stage zones
        // LITE tier users don't have access to Live Tracking, skip validation
        const hasLiveTrackingAccess =
          subscriptionStatus?.plan_tier === "pro" ||
          subscriptionStatus?.plan_tier === "studio" ||
          subscriptionStatus?.manual_producer_access === true;

        if (!hasLiveTrackingAccess) {
          // Skip validation for users without Live Tracking access
          return { valid: true };
        }

        // Refetch festival areas to get latest data (zones may have been added/copied)
        const { data: freshFestivalAreas } = await refetchFestivalAreas();

        // Check that the festival boundary exists
        const hasFestivalBoundary = freshFestivalAreas?.some(
          (zone: Tables<"festival_areas">) => zone.area_type === "festival_boundary",
        );

        if (!hasFestivalBoundary) {
          return {
            valid: false,
            message:
              "Please draw the festival boundary (green zone) before continuing. This is required for live tracking to work correctly.",
          };
        }

        // Check that all stages for all days have zones defined
        const numDays = festivalDays.length;
        const missingZones: string[] = [];

        for (let dayIndex = 0; dayIndex < numDays; dayIndex++) {
          // Get stages for this day
          const dayStages = wizardStagesForDay(stages, dayIndex);

          for (const stage of dayStages) {
            // FK-first zone-completeness check. briefing_stages.id ↔
            // festival_areas.briefing_stage_id is rename-stable; the
            // legacy slug match (zone.stage_id ↔ stage.name) is kept
            // as fallback for zones drawn before the FK backfill.
            const fkId = existingBriefingStages.find(
              (s) => s.label === (stage.label || stage.name),
            )?.id;
            const hasZone = freshFestivalAreas?.some(
              (zone: Tables<"festival_areas"> & { briefing_stage_id?: string | null }) =>
                zone.area_type === "stage_zone" &&
                zone.day_index === dayIndex &&
                ((fkId && zone.briefing_stage_id === fkId) || zone.stage_id === stage.name),
            );

            if (!hasZone) {
              missingZones.push(`Day ${dayIndex + 1}: ${stage.label || stage.name}`);
            }
          }
        }

        if (missingZones.length > 0) {
          return {
            valid: false,
            message: `Missing stage zones for: ${missingZones.join(", ")}. Please draw zones for all stages before continuing.`,
          };
        }
        return { valid: true };
      }

      default:
        return { valid: true };
    }
  };
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        // Basic Information
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div data-guide="step-0-tip-1">
                <Label>Name of festival</Label>
                <Input
                  value={festivalName}
                  onChange={(e) => setFestivalName(e.target.value)}
                  placeholder="Enter festival name"
                  className="border-border bg-card"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Enter location"
                  className="border-border bg-card"
                />
              </div>
            </div>

            <div>
              <Label>Timezone</Label>
              <p className="mb-2 text-sm text-muted-foreground">
                Select the timezone where the festival takes place. This ensures notifications are
                sent at the correct local time.
              </p>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="w-full border-border bg-card md:w-[300px]">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4" data-guide="step-0-tip-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Festival Days</h3>
                <Button variant="outline" onClick={addFestivalDay} size="sm">
                  <Plus className="mr-2 h-4 w-4" /> Add Day
                </Button>
              </div>

              {festivalDays.map((day, index) => (
                <Card key={index} className="border-border bg-card p-4">
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {editingDayNameIndex === index ? (
                        <>
                          <span className="whitespace-nowrap font-medium">Day {index + 1} -</span>
                          <Input
                            autoFocus
                            value={day.name ?? ""}
                            placeholder="Name (optional)"
                            aria-label={`Name for day ${index + 1}`}
                            onChange={(e) => {
                              const newDays = [...festivalDays];
                              newDays[index] = { ...newDays[index], name: e.target.value };
                              setFestivalDays(newDays);
                            }}
                            onBlur={() => setEditingDayNameIndex(null)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setEditingDayNameIndex(null);
                            }}
                            className="h-8 max-w-xs border-border bg-card"
                          />
                        </>
                      ) : (
                        <h4 className="truncate font-medium">
                          {formatDayLabel({ index, customName: day.name })}
                        </h4>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-label={`Edit name for day ${index + 1}`}
                        onClick={() =>
                          setEditingDayNameIndex(editingDayNameIndex === index ? null : index)
                        }
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                    {festivalDays.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeFestivalDay(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={day.date}
                        onChange={(e) => handleDateInputChange(index, e.target.value)}
                        onFocus={() => handleDateFocus(index)}
                        onBlur={() => handleDateBlur(index)}
                        className="border-border bg-card"
                      />
                    </div>
                    <div>
                      <Label>Doors open</Label>
                      <Input
                        type="time"
                        value={day.startTime}
                        onChange={(e) => {
                          const newDays = [...festivalDays];
                          newDays[index].startTime = e.target.value;
                          setFestivalDays(newDays);
                        }}
                        className="border-border bg-card"
                      />
                    </div>
                    <div>
                      <Label>Doors close</Label>
                      <Input
                        type="time"
                        value={day.endTime}
                        onChange={(e) => {
                          const newDays = [...festivalDays];
                          newDays[index].endTime = e.target.value;
                          setFestivalDays(newDays);
                        }}
                        className="border-border bg-card"
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      case 1:
        // Stages per day
        return (
          <div className="space-y-6">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold">Configure stages for each day</h3>
              <p className="text-sm text-muted-foreground">
                Stages are optional — leave a day empty to plan with custom subjects only.
              </p>
            </div>
            <Tabs defaultValue="0" className="w-full" data-guide="step-1-tip-1">
              <TabsList className="h-auto w-full flex-wrap justify-start">
                {festivalDays.map((day, index) => (
                  <TabsTrigger key={index} value={String(index)} className="min-w-[120px]">
                    {formatDayLabel({ index, customName: day.name })}
                  </TabsTrigger>
                ))}
              </TabsList>
              {festivalDays.map((day, dayIndex) => (
                <TabsContent key={dayIndex} value={String(dayIndex)} className="mt-4 space-y-4">
                  <Card className="border-border bg-card p-4">
                    <div className="space-y-4">
                      <div className="mb-4 flex justify-end gap-2">
                        {dayIndex > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const prevDayStages = stages.filter((s) =>
                                s.name.startsWith(`day${dayIndex - 1}_`),
                              );
                              const newStages = prevDayStages.map((stage, idx) => ({
                                name: `day${dayIndex}_stage${idx + 1}`,
                                label: stage.label,
                              }));
                              // Remove existing stages for this day
                              const filteredStages = stages.filter(
                                (s) => !s.name.startsWith(`day${dayIndex}_`),
                              );
                              setStages([...filteredStages, ...newStages]);
                              toast({
                                title: "Stages copied",
                                description: "Stages copied from previous day",
                              });
                            }}
                          >
                            Copy from Previous Day
                          </Button>
                        )}
                        {dayIndex < festivalDays.length - 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const nextDayStages = stages.filter((s) =>
                                s.name.startsWith(`day${dayIndex + 1}_`),
                              );
                              const newStages = nextDayStages.map((stage, idx) => ({
                                name: `day${dayIndex}_stage${idx + 1}`,
                                label: stage.label,
                              }));
                              // Remove existing stages for this day
                              const filteredStages = stages.filter(
                                (s) => !s.name.startsWith(`day${dayIndex}_`),
                              );
                              setStages([...filteredStages, ...newStages]);
                              toast({
                                title: "Stages copied",
                                description: "Stages copied from next day",
                              });
                            }}
                          >
                            Copy from Next Day
                          </Button>
                        )}
                      </div>
                      {stages
                        .filter((stage) => stage.name.startsWith(`day${dayIndex}_`))
                        .map((stage, stageIndex) => (
                          <div key={stageIndex} className="flex gap-2">
                            <Input
                              value={stage.label}
                              onChange={(e) => {
                                const newStages = [...stages];
                                const globalIndex = stages.findIndex((s) => s.name === stage.name);
                                newStages[globalIndex] = {
                                  ...newStages[globalIndex],
                                  label: e.target.value,
                                };
                                setStages(newStages);
                              }}
                              placeholder="Stage name"
                              className="flex-1"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setStages(stages.filter((s) => s.name !== stage.name));
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      <Button
                        variant="outline"
                        onClick={() => {
                          const dayStages = stages.filter((s) =>
                            s.name.startsWith(`day${dayIndex}_`),
                          );
                          const newStageNum = dayStages.length + 1;
                          setStages([
                            ...stages,
                            {
                              name: `day${dayIndex}_stage${newStageNum}`,
                              label: "",
                            },
                          ]);
                        }}
                        className="w-full"
                      >
                        <Plus className="mr-2 h-4 w-4" /> Add Stage
                      </Button>
                    </div>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        );
      case 2:
        // Timetable Import with AI
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Import timetable</h3>
            <p className="text-sm text-muted-foreground">
              Upload screenshots or manually add set times
            </p>

            <Tabs defaultValue="0" className="w-full">
              <TabsList className="h-auto w-full flex-wrap justify-start">
                {festivalDays.map((day, index) => (
                  <TabsTrigger key={index} value={String(index)} className="min-w-[120px]">
                    {formatDayLabel({ index, customName: day.name })}
                  </TabsTrigger>
                ))}
              </TabsList>
              {festivalDays.map((day, dayIndex) => {
                const dayStages = stages.filter((s) => s.name.startsWith(`day${dayIndex}_`));
                const dayTimetable = timetable.filter((l) => l.event_date === day.date);

                return (
                  <TabsContent key={dayIndex} value={String(dayIndex)}>
                    <LineupImportTab
                      day={new Date(day.date)}
                      stages={dayStages.map((s) => ({
                        day: day.date,
                        name: s.label,
                        // Bridge the editor's JSONB stage to its
                        // briefing_stages.id so LineupImportTab's
                        // filters survive a mid-session rename.
                        id: existingBriefingStages.find((bs) => bs.label === s.label)?.id ?? null,
                      }))}
                      timetable={dayTimetable}
                      onMoveRequest={({ proposedTimetable, fromArtist, toArtist, stageName }) => {
                        // Predict the assignment-rebind impact for THIS swap
                        // by running the same helper saveRelatedData uses,
                        // against a hypothetical full timetable.
                        const otherDays = timetable.filter((t) => t.event_date !== day.date);
                        const proposedFull = [...otherDays, ...proposedTimetable];
                        // Use raw stage_name on both sides so the helper
                        // diffs apples-to-apples. Mid-session stage renames
                        // are normalized at save time by saveRelatedData's
                        // local resolveStage; for the pre-save preview we
                        // don't need that normalization because both the
                        // lineup snapshot and the assignment snapshot come
                        // from the same in-memory state.
                        const toLineupSlot = (
                          t: (typeof timetable)[number] | (typeof proposedTimetable)[number],
                        ) => ({
                          artist_name: t.artist_name,
                          stage_name: t.stage_name,
                          event_date: t.event_date,
                          set_start: t.set_start,
                          set_end: t.set_end,
                        });
                        const outcomes = computeAssignmentTimeUpdates(
                          proposedFull.map(toLineupSlot),
                          crewAssignments
                            .filter((a) => a.artist_name)
                            .map((a, i) => ({
                              id: a.id || `pending-${i}`,
                              artist_name: a.artist_name ?? null,
                              stage_name: a.stage_name,
                              event_date:
                                a.event_date ||
                                (festivalDays.length > 0 ? festivalDays[0].date : ""),
                              filming_start: a.filming_start,
                              filming_end: a.filming_end,
                            })),
                          timetable.map(toLineupSlot),
                        );
                        const apply = () => setTimetable(proposedFull);
                        const deactivations = outcomes.filter(
                          (o): o is { id: string; deactivate: true } =>
                            "deactivate" in o && o.deactivate === true,
                        );
                        const writes = outcomes.filter(
                          (
                            o,
                          ): o is {
                            id: string;
                            artist_name?: string;
                            filming_start: string;
                            filming_end: string;
                          } => !("deactivate" in o),
                        );
                        const relabels = writes.filter((o) => o.artist_name !== undefined);
                        const timeMoves = writes.filter((o) => o.artist_name === undefined);
                        if (
                          relabels.length === 0 &&
                          timeMoves.length === 0 &&
                          deactivations.length === 0
                        ) {
                          apply();
                          return;
                        }
                        setPendingMoveSwap({
                          apply,
                          fromArtist,
                          toArtist,
                          stageName,
                          relabeled: relabels.length,
                          timeMoved: timeMoves.length,
                          deactivated: deactivations.length,
                        });
                      }}
                      onTimetableUpdate={(newTimetable) => {
                        // Check for orphaned assignments before updating.
                        // FK-keyed: bridge each timetable entry's
                        // stage_name to its briefing_stages.id so a
                        // producer rename mid-import doesn't false-
                        // positive every artist as "removed".
                        const affectedArtists: string[] = [];
                        const stageIdByLabelLocal = new Map(
                          existingBriefingStages.map((s) => [s.label, s.id]),
                        );
                        const artistFkKey = (t: {
                          artist_name?: string | null;
                          stage_name: string;
                          stage_id?: string | null;
                        }) => {
                          const sId = t.stage_id ?? stageIdByLabelLocal.get(t.stage_name) ?? null;
                          return `${t.artist_name ?? ""}::${sId ? `id:${sId}` : `name:${t.stage_name}`}`;
                        };
                        const newArtistKeys = new Set(newTimetable.map((t) => artistFkKey(t)));
                        const oldArtistKeys = new Set(dayTimetable.map((t) => artistFkKey(t)));

                        // Find removed or rescheduled artists that have assignments
                        crewAssignments.forEach((assignment) => {
                          if (assignment.assignment_type === "artist" && assignment.artist_name) {
                            const key = artistFkKey({
                              artist_name: assignment.artist_name,
                              stage_name: assignment.stage_name,
                              stage_id: (assignment as { stage_id?: string | null }).stage_id,
                            });
                            if (oldArtistKeys.has(key) && !newArtistKeys.has(key)) {
                              affectedArtists.push(assignment.artist_name);
                            }
                          }
                        });

                        // Remove assignments for affected artists
                        if (affectedArtists.length > 0) {
                          const updatedAssignments = crewAssignments.filter(
                            (a) => !affectedArtists.includes(a.artist_name || ""),
                          );
                          setCrewAssignments(updatedAssignments);

                          toast({
                            title: "Warning: Assigned tasks removed",
                            description: `${affectedArtists.length} artist(s) were rescheduled and their crew assignments have been removed: ${affectedArtists.join(", ")}`,
                            variant: "destructive",
                          });
                        }

                        // Remove old timetable for this day and add new
                        const otherDaysTimetable = timetable.filter(
                          (l) => l.event_date !== day.date,
                        );
                        setTimetable([...otherDaysTimetable, ...newTimetable]);
                      }}
                      onAiExtractionUsed={() => setUsedAiExtraction(true)}
                    />
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        );
      case 3: {
        // The crew
        // newlyAddedCrewMembers is keyed per (email, event_date) but the
        // banner reminds the producer to invite a *person* — show one
        // bullet per unique email so a 3-day shift doesn't render
        // "Testproducer (testproducer@…)" three times.
        const newlyAddedCrewList = (() => {
          const seen = new Set<string>();
          const list: typeof crew = [];
          for (const member of crew) {
            const trackingKey = `${member.email}-${member.event_date}`;
            if (!newlyAddedCrewMembers.has(trackingKey)) continue;
            const personKey = (member.email ?? "").trim().toLowerCase();
            if (!personKey || seen.has(personKey)) continue;
            seen.add(personKey);
            list.push(member);
          }
          return list;
        })();

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Add crew members</h3>
                <p className="text-sm text-muted-foreground">
                  Add crew members for each festival day
                </p>
              </div>
              {isPublished && currentBriefingId && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/productions/${currentBriefingId}/crew`)}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Go to Invite
                </Button>
              )}
            </div>

            {/* My Team reminder banner - shown per production */}
            {(() => {
              const tipKey = effectiveId
                ? `myteam-tip-dismissed-${effectiveId}`
                : "myteam-tip-dismissed";
              const dismissed =
                typeof window !== "undefined" && safeLocalStorage.getItem(tipKey) === "true";
              if (dismissed) return null;
              return (
                <Card
                  className="flex items-center justify-between gap-3 border-accent/20 bg-accent/10 p-3"
                  data-myteam-tip
                >
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 shrink-0 text-accent" />
                    <span>
                      <span className="font-medium">Tip:</span> Save your frequent crew members in{" "}
                      <button
                        type="button"
                        className="text-primary underline hover:text-primary/80"
                        onClick={() => navigate("/my-team")}
                      >
                        My Team
                      </button>{" "}
                      to speed up adding them here.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      safeLocalStorage.setItem(tipKey, "true");
                      const el = document.querySelector("[data-myteam-tip]");
                      if (el) (el as HTMLElement).style.display = "none";
                    }}
                    data-myteam-tip-close
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Card>
              );
            })()}

            {/* Reminder banner for newly added crew members */}
            {newlyAddedCrewList.length > 0 && (
              <Card className="border-primary/20 bg-primary/10 p-4">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 text-primary" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium">New crew member(s) added</h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Don't forget to invite the following crew member(s):
                    </p>
                    <ul className="mt-2 space-y-1 text-sm">
                      {newlyAddedCrewList.map((member, idx) => (
                        <li key={idx} className="text-muted-foreground">
                          • {member.name} ({member.email})
                        </li>
                      ))}
                    </ul>
                    {isPublished && currentBriefingId && (
                      <Button
                        size="sm"
                        className="mt-3"
                        onClick={async () => {
                          // Save first, then navigate. This is the "added
                          // crew to a published briefing" flow — only the
                          // crew section's tables actually need writing.
                          try {
                            // Before the first write, as in handleSave.
                            const checkedScheduleRevision = await guardScheduleRevision("crew");
                            await saveProduction(
                              getBriefingData(),
                              isPublished ? "published" : "draft",
                            );
                            await saveRelatedData(undefined, "crew", checkedScheduleRevision);
                          } catch (error) {
                            // Stay on the page. guardScheduleRevision,
                            // saveProduction and a schedule conflict in
                            // saveRelatedData show their own message.
                            console.error(error);
                            return;
                          }
                          setNewlyAddedCrewMembers(new Set());

                          // Invalidate crew-members cache before navigating
                          queryClient.invalidateQueries({
                            queryKey: ["crew-members", currentBriefingId],
                          });

                          navigate(`/productions/${currentBriefingId}/crew`);
                        }}
                      >
                        <Save className="mr-2 h-4 w-4" />
                        Save & Go to Invite
                      </Button>
                    )}
                    {!isPublished && (
                      <p className="mt-3 text-xs italic text-muted-foreground">
                        You can invite crew members after publishing the production.
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            )}

            <div className="space-y-3">
              {(() => {
                // Group flat per-day crew rows into one card per person.
                // Identity is _personId (stable across email edits), or
                // lower(email) for legacy rows that haven't been
                // backfilled, or `__blank-${idx}__` for rows lacking
                // both (shouldn't happen post-load, but defensive).
                type Group = {
                  key: string;
                  personId?: string;
                  email: string;
                  name: string;
                  phone: string | null;
                  function: string;
                  color: string | null;
                  hasColor: boolean;
                  isExistingUser?: boolean;
                  rowsByDay: Map<string, CrewMember>;
                  rowIndicesByDay: Map<string, number>;
                };
                const groups: Group[] = [];
                const seen = new Map<string, number>();
                crew.forEach((m, idx) => {
                  const k = crewGroupKeyOf(m, idx);
                  let gi = seen.get(k);
                  if (gi === undefined) {
                    gi = groups.length;
                    seen.set(k, gi);
                    groups.push({
                      key: k,
                      personId: m._personId,
                      email: m.email,
                      name: m.name,
                      phone: m.phone || null,
                      function: m.function,
                      color: m.color || null,
                      hasColor: !!m.hasColor,
                      isExistingUser: m.isExistingUser,
                      rowsByDay: new Map(),
                      rowIndicesByDay: new Map(),
                    });
                  }
                  const g = groups[gi];
                  g.rowsByDay.set(m.event_date, m);
                  g.rowIndicesByDay.set(m.event_date, idx);
                });

                if (groups.length === 0) {
                  return (
                    <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                      No crew members yet. Click "Add crew member" below to start.
                    </div>
                  );
                }

                return groups.map((group) => {
                  const expanded = expandedCrewIds.has(group.key);
                  const editing = !isPublished || editingCrewIds.has(group.key);
                  const repIdxRaw = group.rowIndicesByDay.values().next().value;
                  if (repIdxRaw === undefined) return null;
                  const repIdx: number = repIdxRaw;
                  const checkedDays = new Set(group.rowsByDay.keys());
                  const isNewlyAdded = Array.from(group.rowsByDay.values()).some((r) =>
                    newlyAddedCrewMembers.has(`${r.email}-${r.event_date}`),
                  );
                  return (
                    <Collapsible
                      key={group.key}
                      open={expanded}
                      onOpenChange={(open) =>
                        setExpandedCrewIds((prev) => {
                          const next = new Set(prev);
                          if (open) next.add(group.key);
                          else next.delete(group.key);
                          return next;
                        })
                      }
                    >
                      <Card className="overflow-hidden border-border bg-card">
                        <div className="flex items-center gap-2 p-4">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex flex-1 items-center gap-3 text-left"
                            >
                              <ChevronDown
                                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`}
                              />
                              {group.hasColor && group.color && (
                                <div
                                  className="h-4 w-4 shrink-0 rounded-full"
                                  style={{ backgroundColor: group.color }}
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="truncate font-medium">
                                    {group.name || "(no name)"}
                                  </p>
                                  {isNewlyAdded && (
                                    <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                <p className="truncate text-sm text-muted-foreground">
                                  {group.email || "(email)"}
                                  {(() => {
                                    const roles = new Set(
                                      Array.from(group.rowsByDay.values())
                                        .map((r) => r.function)
                                        .filter((f) => f && f.trim() !== ""),
                                    );
                                    if (roles.size === 1) {
                                      return <> · {Array.from(roles)[0]}</>;
                                    }
                                    if (roles.size > 1) return <> · varies by day</>;
                                    return null;
                                  })()}
                                  {festivalDays.length > 0 && (
                                    <>
                                      {" "}
                                      · {checkedDays.size}/{festivalDays.length} day
                                      {festivalDays.length === 1 ? "" : "s"}
                                    </>
                                  )}
                                </p>
                              </div>
                            </button>
                          </CollapsibleTrigger>
                          <AddToTeamButton
                            member={{
                              name: group.name,
                              email: group.email,
                              phone: group.phone,
                              function: group.function,
                            }}
                            existing={teamMembers ?? []}
                            isPending={addToTeamMutation.isPending}
                            onAdd={(form) => addToTeamMutation.mutate(form)}
                          />
                          {isPublished && !editingCrewIds.has(group.key) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCrewIds((prev) => new Set([...prev, group.key]));
                              }}
                            >
                              <Pencil className="mr-1 h-4 w-4" />
                              Edit
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePerson(group);
                            }}
                            aria-label={`Remove ${group.name || group.email || "crew member"}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <CollapsibleContent>
                          <div className="space-y-4 border-t border-border bg-muted/10 p-4">
                            {editing ? (
                              <>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="relative">
                                    <Label className="text-xs">Name</Label>
                                    <Input
                                      placeholder="Name"
                                      value={group.name}
                                      onChange={(e) => {
                                        setCrew(
                                          applyPersonFieldAcrossDays(crew, repIdx, {
                                            name: e.target.value,
                                          }),
                                        );
                                        setActiveAutocompleteIndex(
                                          e.target.value.length >= 2 ? repIdx : null,
                                        );
                                      }}
                                      onFocus={() => {
                                        if (group.name.length >= 2)
                                          setActiveAutocompleteIndex(repIdx);
                                      }}
                                      onBlur={() => {
                                        setTimeout(() => setActiveAutocompleteIndex(null), 200);
                                      }}
                                      autoComplete="off"
                                    />
                                    {activeAutocompleteIndex === repIdx &&
                                      teamMembers &&
                                      (() => {
                                        const val = group.name.toLowerCase();
                                        const suggestions = teamMembers.filter(
                                          (tm: Tables<"team_members">) =>
                                            tm.name.toLowerCase().includes(val),
                                        );
                                        if (suggestions.length === 0) return null;
                                        return (
                                          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-40 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                                            {suggestions.map((s: Tables<"team_members">) => (
                                              <button
                                                key={s.id}
                                                type="button"
                                                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-primary/20"
                                                onMouseDown={(e) => {
                                                  e.preventDefault();
                                                  setCrew(
                                                    applyPersonFieldAcrossDays(crew, repIdx, {
                                                      name: s.name,
                                                      email: s.email,
                                                      phone: s.phone || "",
                                                      function: s.role || "",
                                                    }),
                                                  );
                                                  setActiveAutocompleteIndex(null);
                                                }}
                                              >
                                                <span className="font-medium">{s.name}</span>
                                                <span className="text-xs capitalize text-muted-foreground">
                                                  {s.role || ""}
                                                </span>
                                              </button>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                  </div>
                                  <div>
                                    <Label className="text-xs">Email *</Label>
                                    <Input
                                      placeholder="Email"
                                      type="email"
                                      value={group.email}
                                      onChange={(e) =>
                                        setCrew(
                                          applyPersonFieldAcrossDays(crew, repIdx, {
                                            email: e.target.value,
                                          }),
                                        )
                                      }
                                      onBlur={async (e) => {
                                        const email = e.target.value.trim().toLowerCase();
                                        if (email && email.includes("@")) {
                                          const { data } = await supabase.rpc(
                                            "lookup_profile_by_email",
                                            { lookup_email: email },
                                          );
                                          const isExistingUser = !!(data && data.length > 0);
                                          // Re-find by email since the
                                          // change may have re-grouped.
                                          setCrew((prev) =>
                                            prev.map((row) =>
                                              (row.email ?? "").trim().toLowerCase() === email
                                                ? { ...row, isExistingUser }
                                                : row,
                                            ),
                                          );
                                        }
                                      }}
                                    />
                                    {group.isExistingUser !== undefined && (
                                      <span
                                        className={`mt-1 text-xs ${group.isExistingUser ? "text-primary" : "text-muted-foreground"}`}
                                      >
                                        {group.isExistingUser ? "PROduce user" : "New user"}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <Label className="text-xs">Phone number</Label>
                                  <Input
                                    placeholder="Phone number"
                                    type="tel"
                                    value={group.phone ?? ""}
                                    onChange={(e) =>
                                      setCrew(
                                        applyPersonFieldAcrossDays(crew, repIdx, {
                                          phone: e.target.value,
                                        }),
                                      )
                                    }
                                  />
                                </div>

                                <div>
                                  <Label className="text-xs">Working days</Label>
                                  <p className="mb-2 text-xs text-muted-foreground">
                                    Role and arrival time are per day — same person can have a
                                    different role on day 1 vs day 2.
                                  </p>
                                  <div className="space-y-2">
                                    {festivalDays.map((day, dayIdx) => {
                                      const checked = checkedDays.has(day.date);
                                      const row = group.rowsByDay.get(day.date);
                                      const idForDay = group.rowIndicesByDay.get(day.date);
                                      return (
                                        <div
                                          key={dayIdx}
                                          className="flex flex-wrap items-center gap-2 rounded border border-border bg-background p-2"
                                        >
                                          <Checkbox
                                            id={`${group.key}-day-${dayIdx}`}
                                            checked={checked}
                                            onCheckedChange={(c) => {
                                              handleToggleDayForPerson(group, day, c === true);
                                            }}
                                          />
                                          <Label
                                            htmlFor={`${group.key}-day-${dayIdx}`}
                                            className="min-w-[110px] flex-1 cursor-pointer text-sm"
                                          >
                                            Day {dayIdx + 1} — {day.date}
                                          </Label>
                                          {checked && row && idForDay !== undefined && (
                                            <>
                                              <Select
                                                value={row.function ?? ""}
                                                onValueChange={(value) => {
                                                  const newCrew = [...crew];
                                                  newCrew[idForDay] = {
                                                    ...newCrew[idForDay],
                                                    function: value,
                                                  };
                                                  setCrew(newCrew);
                                                }}
                                              >
                                                <SelectTrigger className="h-9 w-[160px] bg-background">
                                                  <SelectValue placeholder="Select role" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-popover">
                                                  {CREW_ROLES.map((role) => (
                                                    <SelectItem key={role} value={role}>
                                                      {role}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <div className="flex items-center gap-1">
                                                <Clock className="h-3 w-3 text-muted-foreground" />
                                                <Input
                                                  type="time"
                                                  className="h-9 w-28"
                                                  value={row.arrival_time || ""}
                                                  onChange={(e) => {
                                                    const newCrew = [...crew];
                                                    newCrew[idForDay] = {
                                                      ...newCrew[idForDay],
                                                      arrival_time: e.target.value,
                                                    };
                                                    setCrew(newCrew);
                                                  }}
                                                />
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                {group.hasColor ? (
                                  <div className="flex items-center gap-3">
                                    <Label className="text-xs">Color</Label>
                                    <input
                                      type="color"
                                      value={group.color || DEFAULT_COLORS[0]}
                                      onChange={(e) =>
                                        setCrew(
                                          applyPersonFieldAcrossDays(crew, repIdx, {
                                            color: e.target.value,
                                          }),
                                        )
                                      }
                                      className="h-10 w-20 cursor-pointer rounded border border-input"
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setCrew(
                                          applyPersonFieldAcrossDays(crew, repIdx, {
                                            hasColor: false,
                                          }),
                                        )
                                      }
                                    >
                                      Remove color
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const colorIndex =
                                        groups.filter((g) => g.hasColor).length %
                                        DEFAULT_COLORS.length;
                                      setCrew(
                                        applyPersonFieldAcrossDays(crew, repIdx, {
                                          hasColor: true,
                                          color: DEFAULT_COLORS[colorIndex],
                                        }),
                                      );
                                    }}
                                  >
                                    Assign Color
                                  </Button>
                                )}

                                {isPublished && (
                                  <div className="flex justify-end">
                                    <Button
                                      size="sm"
                                      style={{ backgroundColor: "#d1ff38", color: "#000" }}
                                      className="hover:opacity-90"
                                      onClick={() => {
                                        // Just exit edit mode. We used to mark
                                        // every day-row as "needs invite" here,
                                        // but a producer editing existing
                                        // crew (e.g. role tweak) does not need
                                        // to re-invite. Genuinely-new people
                                        // should come through ManageCrew's
                                        // explicit Invite flow.
                                        setEditingCrewIds((prev) => {
                                          const next = new Set(prev);
                                          next.delete(group.key);
                                          return next;
                                        });
                                      }}
                                    >
                                      <CheckCircle2 className="mr-1 h-4 w-4" />
                                      Done
                                    </Button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="space-y-3 text-sm">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="text-muted-foreground">Email: </span>
                                    <span>{group.email || "-"}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Phone: </span>
                                    <span>{group.phone || "-"}</span>
                                  </div>
                                </div>
                                {group.rowsByDay.size > 0 && (
                                  <div>
                                    <span className="text-muted-foreground">Shifts: </span>
                                    <div className="mt-1 space-y-1">
                                      {festivalDays
                                        .filter((d) => group.rowsByDay.has(d.date))
                                        .map((d) => {
                                          const r = group.rowsByDay.get(d.date)!;
                                          return (
                                            <div
                                              key={d.date}
                                              className="flex items-center gap-3 rounded bg-background/50 p-2"
                                            >
                                              <span className="min-w-[110px] font-medium">
                                                {d.date}
                                              </span>
                                              <span className="flex-1">
                                                {r.function || (
                                                  <span className="text-muted-foreground">
                                                    no role
                                                  </span>
                                                )}
                                              </span>
                                              {r.arrival_time && (
                                                <span className="flex items-center gap-1 text-muted-foreground">
                                                  <Clock className="h-3 w-3" />
                                                  {formatTime(r.arrival_time)}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  );
                });
              })()}
            </div>

            <Button
              variant="outline"
              onClick={addPersonAcrossDays}
              className="w-full"
              data-guide="step-3-tip-1"
            >
              <Plus className="mr-2 h-4 w-4" /> Add crew member
            </Button>

            <AlertDialog
              open={!!pendingCrewRemoval}
              onOpenChange={(open) => {
                if (!open) setPendingCrewRemoval(null);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {pendingCrewRemoval?.kind === "person"
                      ? `Remove ${pendingCrewRemoval.label}?`
                      : `Remove ${pendingCrewRemoval?.label ?? "this day"}?`}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {pendingCrewRemoval && pendingCrewRemoval.assignmentCount > 0 ? (
                      <>
                        This will also delete <strong>{pendingCrewRemoval.assignmentCount}</strong>{" "}
                        crew assignment
                        {pendingCrewRemoval.assignmentCount === 1 ? "" : "s"} linked to this row.
                      </>
                    ) : (
                      "Continue?"
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      pendingCrewRemoval?.confirm();
                      setPendingCrewRemoval(null);
                    }}
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      }
      case 4:
        // Festival Map
        return (
          <div className="mx-auto max-w-2xl space-y-6 overflow-hidden">
            <h3 className="text-lg font-semibold">Festival map</h3>
            <p className="text-sm text-muted-foreground">Upload a map image for crew reference</p>

            <div
              className="w-full space-y-4 rounded-lg border-2 border-dashed border-border p-12 text-center"
              data-guide="step-4-tip-1"
            >
              {!mapFile && !mapStoragePath && (
                <>
                  <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="mb-4 text-muted-foreground">
                    Upload the festival map for crew reference
                  </p>
                  <div className="mx-auto max-w-xs">
                    <Label
                      htmlFor="map-upload"
                      className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-glow"
                    >
                      Choose file
                    </Label>
                    <Input
                      id="map-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        if (file) {
                          // Reject too-large / wrong-typed maps up front so the
                          // user gets an instant, clear message instead of a
                          // failed 36 MB upload (see upload-briefing-map 413).
                          const validation = validateFestivalMapFile(file);
                          if (!validation.ok) {
                            toast({
                              title: "Couldn't add map",
                              description: validation.message,
                              variant: "destructive",
                            });
                            e.target.value = "";
                            return;
                          }
                        }
                        setMapFile(file);
                      }}
                      className="sr-only"
                    />
                  </div>
                </>
              )}
              {mapFile && (
                <div className="max-w-full space-y-3 overflow-hidden">
                  <img
                    src={URL.createObjectURL(mapFile)}
                    alt="Festival map preview"
                    className="mx-auto max-h-[500px] w-full rounded-lg object-contain"
                  />
                  <p className="text-sm text-muted-foreground">{mapFile.name}</p>
                  <Button variant="outline" onClick={() => setMapFile(null)} className="mt-2">
                    <X className="mr-2 h-4 w-4" />
                    Remove Map
                  </Button>
                </div>
              )}
              {!mapFile && mapStoragePath && (
                <FestivalMapPreview
                  storagePath={mapStoragePath}
                  onRemove={() => setMapStoragePath(null)}
                />
              )}
            </div>
          </div>
        );
      case 5:
        // Crew Schedules
        return (
          <CrewSchedule
            festivalDays={festivalDays}
            stages={stages}
            crew={crew}
            timetable={timetable}
            assignments={crewAssignments}
            onAssignmentsUpdate={setCrewAssignments}
            onCustomSubjectsUpdate={setCustomSubjects}
            briefingId={productionId || currentBriefingId || ""}
            onSave={async () => {
              // Step 5's schedule editor only writes crew_assignments (via the
              // crew section's STEP-6 sub-block). Scope to "crew" so we don't
              // re-run stages/lineup/doors on a schedule save.
              try {
                await handleSave(
                  existingBriefing?.status === "published" ? "published" : "draft",
                  false,
                  stepScope(currentStep),
                );
              } catch (error) {
                // A phone edit stopped the save and the producer was already
                // told to reload: don't let CrewSchedule add its generic
                // "Failed to save schedule" (it shows nothing on success).
                if (error instanceof ScheduleChangedElsewhereError) return;
                throw error;
              }
            }}
            defaultAssignmentLength={producerSettings?.default_assignment_length || 15}
          />
        );
      case 6:
        // General Briefing
        return (
          <div className="space-y-6">
            <Card className="bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">General</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name of festival</Label>
                  <Input value={festivalName} onChange={(e) => setFestivalName(e.target.value)} />
                </div>
                <div>
                  <Label>Festival date start</Label>
                  <Input
                    type="date"
                    value={festivalDateStart}
                    onChange={(e) => setFestivalDateStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Festival date end</Label>
                  <Input
                    type="date"
                    value={festivalDateEnd}
                    onChange={(e) => setFestivalDateEnd(e.target.value)}
                  />
                </div>
                <div className="col-span-2" data-guide="step-6-tip-1">
                  <Label>Navigation address *</Label>
                  <Input
                    value={navigationAddress}
                    onChange={(e) => setNavigationAddress(e.target.value)}
                    placeholder="Enter the full address for crew navigation"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Hotel address</Label>
                  <Input
                    value={hotelAddress}
                    onChange={(e) => setHotelAddress(e.target.value)}
                    placeholder="Enter the hotel address for the crew (optional)"
                  />
                </div>
              </div>
            </Card>

            {/* Weather forecast — appears once a navigation address is set;
                between General and Crew catering. All festival days shown. */}
            <WeatherCard
              briefingId={effectiveId}
              hasLocation={!!navigationAddress}
              perDayDoors={existingDayDoors ?? []}
              fallbackDoors={{
                doors_open: existingBriefing?.doors_open ?? null,
                doors_close: existingBriefing?.doors_close ?? null,
              }}
            />

            <Card className="bg-card p-6" data-guide="step-6-tip-2">
              <h3 className="mb-4 text-lg font-semibold">Crew catering</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Switch checked={lunchEnabled} onCheckedChange={setLunchEnabled} />
                      <Label>Lunch</Label>
                    </div>
                    {lunchEnabled && (
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={lunch.start}
                          onChange={(e) => setLunch({ ...lunch, start: e.target.value })}
                        />
                        <Input
                          type="time"
                          value={lunch.end}
                          onChange={(e) => setLunch({ ...lunch, end: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Switch checked={dinnerEnabled} onCheckedChange={setDinnerEnabled} />
                      <Label>Dinner</Label>
                    </div>
                    {dinnerEnabled && (
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={dinner.start}
                          onChange={(e) => setDinner({ ...dinner, start: e.target.value })}
                        />
                        <Input
                          type="time"
                          value={dinner.end}
                          onChange={(e) => setDinner({ ...dinner, end: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <Switch checked={nightsnackEnabled} onCheckedChange={setNightsnackEnabled} />
                      <Label>Night snack</Label>
                    </div>
                    {nightsnackEnabled && (
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={nightsnack.start}
                          onChange={(e) => setNightsnack({ ...nightsnack, start: e.target.value })}
                        />
                        <Input
                          type="time"
                          value={nightsnack.end}
                          onChange={(e) => setNightsnack({ ...nightsnack, end: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Flights — between catering and producer info: it is
                logistics for the crew, not technical settings. Off by
                default; most productions never need it. */}
            <FlightsSection
              briefingId={effectiveId}
              enabled={flightsEnabled}
              onEnabledChange={setFlightsEnabled}
            />

            <Card className="bg-card p-6" data-guide="step-6-tip-3">
              <h3 className="mb-4 text-lg font-semibold">Producer information</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                This information will be included in the crew briefing.
              </p>
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="step7-company" className="text-sm font-medium">
                    Company name
                  </Label>
                  <Input
                    id="step7-company"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder={producerProfile?.company_name || "Not set in settings"}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="step7-producer-name" className="text-sm font-medium">
                    Producer name
                  </Label>
                  <Input
                    id="step7-producer-name"
                    value={producerName || producerProfile?.full_name || ""}
                    onChange={(e) => setProducerName(e.target.value)}
                    placeholder={producerProfile?.full_name || "Not set in settings"}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="step7-producer-phone" className="text-sm font-medium">
                    Phone number
                  </Label>
                  <Input
                    id="step7-producer-phone"
                    value={producerPhone || producerProfile?.phone || ""}
                    onChange={(e) => setProducerPhone(e.target.value)}
                    placeholder={producerProfile?.phone || "Not set in settings"}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="step7-producer-email" className="text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    id="step7-producer-email"
                    value={producerEmail || producerProfile?.email || ""}
                    onChange={(e) => setProducerEmail(e.target.value)}
                    placeholder={producerProfile?.email || "Not set in settings"}
                    className="bg-background"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="step7-invoice-info" className="text-sm font-medium">
                    Invoice information
                  </Label>
                  <Textarea
                    id="step7-invoice-info"
                    value={invoiceInformation || producerProfile?.invoice_information || ""}
                    onChange={(e) => setInvoiceInformation(e.target.value)}
                    placeholder={producerProfile?.invoice_information || "Not set in settings"}
                    className="min-h-[80px] bg-background"
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Additional information</h3>
              <div className="space-y-4">
                <div>
                  <Label>Crew dresscode</Label>
                  <Textarea
                    value={crewDresscode}
                    onChange={(e) => setCrewDresscode(e.target.value)}
                    placeholder="Black clothing, no own branding."
                  />
                </div>
                <div>
                  <Label>Crew check-in</Label>
                  <Textarea
                    value={crewCheckin}
                    onChange={(e) => setCrewCheckin(e.target.value)}
                    placeholder="Don't forget your ID for the crew check-in."
                  />
                </div>
              </div>
            </Card>

            <Card className="bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Settings</h3>
              <div className="space-y-4">
                <div>
                  <Label>Camera settings</Label>
                  <Textarea
                    value={cameraSettings}
                    onChange={(e) => setCameraSettings(e.target.value)}
                    placeholder="Fill in your camera settings here."
                  />
                </div>
                <div>
                  <Label>Audio settings</Label>
                  <Textarea
                    value={audioSettings}
                    onChange={(e) => setAudioSettings(e.target.value)}
                    placeholder="Fill in your audio settings here."
                  />
                </div>
                <div>
                  <Label>Extra settings</Label>
                  <Textarea
                    value={extraSettings}
                    onChange={(e) => setExtraSettings(e.target.value)}
                    placeholder="If there are any extra technical settings to share, you can add them here."
                  />
                </div>
              </div>
            </Card>

            <CameraIdCard rows={cameraIdRows} onChange={handleCameraIdChange} />

            <Card className="bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold">Restrictions & instructions</h3>
              <div className="space-y-4">
                <div>
                  <Label>Restrictions</Label>
                  <Textarea
                    value={restrictions}
                    onChange={(e) => setRestrictions(e.target.value)}
                    placeholder="If there are on-stage restrictions for certain artists or other important limitations, fill them in here."
                  />
                </div>
                <div>
                  <Label>General instructions</Label>
                  <Textarea
                    value={generalInstructions}
                    onChange={(e) => setGeneralInstructions(e.target.value)}
                    placeholder="Add general crew instructions here."
                    rows={6}
                  />
                </div>
                <div>
                  <Label>Others</Label>
                  <Textarea
                    value={others}
                    onChange={(e) => setOthers(e.target.value)}
                    placeholder="Other important information."
                    rows={4}
                  />
                </div>
              </div>
            </Card>

            <VideoExamplesEditor examples={videoExamples} onChange={setVideoExamples} />

            <Card className="bg-card p-6">
              <div className="mb-4 flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold">Extra fields</h3>
                  <p className="text-sm text-muted-foreground">
                    Add your own briefing cards — e.g. a{" "}
                    <span className="font-medium">Flights</span> field with flight details
                    underneath. These are shown to the crew.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCustomFields([
                      ...customFields,
                      { id: crypto.randomUUID(), title: "", content: "" },
                    ])
                  }
                >
                  <Plus className="mr-2 h-4 w-4" /> Add field
                </Button>
              </div>
              {customFields.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No extra fields yet. Use “Add field” to create one.
                </p>
              ) : (
                <div className="space-y-4">
                  {customFields.map((field) => (
                    <div
                      key={field.id}
                      className="space-y-3 rounded-md border border-border bg-background p-4"
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          value={field.title}
                          placeholder="Field title (e.g. Flights)"
                          onChange={(e) =>
                            setCustomFields(
                              customFields.map((f) =>
                                f.id === field.id ? { ...f, title: e.target.value } : f,
                              ),
                            )
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove field"
                          onClick={() =>
                            setCustomFields(customFields.filter((f) => f.id !== field.id))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <Textarea
                        value={field.content}
                        placeholder="Information for this field…"
                        rows={4}
                        onChange={(e) =>
                          setCustomFields(
                            customFields.map((f) =>
                              f.id === field.id ? { ...f, content: e.target.value } : f,
                            ),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        );
      case 7:
        // Live Tracking Setup
        return (
          <LiveTrackingSetup
            navigationAddress={navigationAddress}
            stages={stages}
            // The actual date of each festival day, in order. Not a
            // start/end pair the editor re-derives: a producer can give any
            // single day any date (handleDateInputChange), so "start + N
            // days" can name a date no briefing_stages row has — and then
            // the stage lookup returns null and the zone lands orphaned,
            // which is the bug this screen is being fixed for.
            festivalDayDates={festivalDays.map((d) => d.date)}
            onSave={(data) => {
              setMapCenter(data.mapCenter);
              setMapZoom(data.mapZoom);
              setFestivalBoundary(data.festivalBoundary);
              setStageZones(data.stageZones);

              toast({
                title: "Live tracking setup saved",
                description: "Your live tracking configuration has been saved.",
              });
            }}
            initialData={{
              mapCenter,
              mapZoom,
              festivalBoundary,
              stageZones,
            }}
          />
        );
      case 8:
        // Review & publish — rendered by a dedicated component so the
        // recap can be tested without booting the whole editor.
        return (
          <ReviewPublishStep
            formatTime={formatTime}
            briefingId={effectiveId}
            festivalName={festivalName}
            clientName={clientName}
            location={location}
            navigationAddress={navigationAddress}
            hotelAddress={hotelAddress}
            crewCheckin={crewCheckin}
            festivalDays={festivalDays}
            dayDoors={existingDayDoors ?? []}
            fallbackDoors={{
              doors_open: existingBriefing?.doors_open ?? null,
              doors_close: existingBriefing?.doors_close ?? null,
            }}
            stages={stages}
            producerProfile={producerProfile}
            crewDresscode={crewDresscode}
            meals={[
              { label: "Lunch", enabled: lunchEnabled, ...lunch },
              { label: "Dinner", enabled: dinnerEnabled, ...dinner },
              { label: "Night snack", enabled: nightsnackEnabled, ...nightsnack },
            ]}
            flightsEnabled={flightsEnabled}
            cameraSettings={cameraSettings}
            audioSettings={audioSettings}
            extraSettings={extraSettings}
            restrictions={restrictions}
            generalInstructions={generalInstructions}
            others={others}
            customFields={visibleEditorCustomFields}
            videoExamples={filledEditorVideoExamples}
            crew={crew}
            selectedDayIndex={reviewDayIndex}
            onSelectDay={setReviewDayIndex}
          />
        );
      default:
        return null;
    }
  };

  // Quick-edit (shortcut) sessions back out to the production overview;
  // the full creation wizard keeps the dashboard as its home.
  const editingProduction = isShortcut && effectiveId;
  const sidebarBackTo = editingProduction ? productionOverviewPath(effectiveId) : "/dashboard";
  const sidebarBackLabel = editingProduction ? "Back to production" : "Back to Dashboard";

  // "Continue anyway" on a validation-warning dialog: save the current
  // step, then either return to the production (quick-edit) or advance the
  // wizard (full editor). Shared by the empty-lineup and unassigned-artists
  // dialogs.
  const continueFromWarning = async (closeDialog: () => void) => {
    closeDialog();
    try {
      // Preserve the briefing's published state — a quick-edit that trips a
      // warning must not silently downgrade a published production to draft.
      await handleSave(
        existingBriefing?.status === "published" ? "published" : "draft",
        false,
        stepScope(currentStep),
      );
      const action = resolveWarningContinue(isShortcut, effectiveId, currentStep);
      if (action.type === "return") {
        navigate(action.path);
      } else {
        setCurrentStep(action.step);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex min-h-screen overflow-x-hidden">
      <AppSidebar backTo={sidebarBackTo} backLabel={sidebarBackLabel} />

      <main
        className="flex-1 overflow-x-hidden bg-muted/20 p-8 transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        <div className={cn("mx-auto w-full", currentStep === 5 ? "max-w-none" : "max-w-6xl")}>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Step {currentStep + 1}</span>
                  <span>of</span>
                  <span>{STEPS.length}</span>
                </div>
                <h1 className="text-2xl font-semibold">
                  {currentStep === 0 ? "Start your new production" : STEPS[currentStep]}
                </h1>
              </div>

              {/* Download PDF Button */}
              {((currentStep === 6 && existingBriefing?.status === "published") ||
                currentStep === 8) && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      setIsGeneratingPDF(true);

                      const response = await fetchWithTimeout(
                        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-briefing-pdf`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${session?.access_token}`,
                          },
                          body: JSON.stringify({ briefingId: effectiveId }),
                        },
                      );

                      if (!response.ok) {
                        throw new Error("Failed to generate PDF");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `PROduce - ${existingBriefing?.festival_name || "Briefing"} - Briefing.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      toast({
                        title: "Success",
                        description: "Briefing PDF downloaded successfully",
                      });
                    } catch (error) {
                      console.error(error);
                      toast({
                        title: "Error",
                        description: "Failed to download PDF",
                        variant: "destructive",
                      });
                    } finally {
                      setIsGeneratingPDF(false);
                    }
                  }}
                  disabled={isGeneratingPDF}
                  className="gap-2"
                  data-guide="step-8-tip-1"
                >
                  <Download className="h-4 w-4" />
                  {isGeneratingPDF ? "Generating PDF..." : "Download briefing (PDF)"}
                </Button>
              )}
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8 flex gap-2">
            {STEPS.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-all ${index <= currentStep ? "bg-primary" : "bg-border"}`}
              />
            ))}
          </div>

          {/* Step navigation (left) alongside the content + Prev/Next buttons */}
          <div className="flex gap-8">
            {!isShortcut &&
              (stepperCollapsed ? (
                <div className="hidden shrink-0 lg:block">
                  <Button
                    variant="outline"
                    size="icon"
                    className="sticky top-8"
                    onClick={() => setStepperCollapsed(false)}
                    title="Show steps"
                    aria-label="Show steps"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <aside className="hidden w-64 shrink-0 lg:block">
                  <div className="sticky top-8 space-y-2">
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setStepperCollapsed(true)}
                        title="Hide steps"
                        aria-label="Hide steps"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </div>
                    <WizardStepper
                      steps={STEPS}
                      currentStep={currentStep}
                      maxUnlockedStep={maxStepReached}
                      onStepClick={handleStepNavClick}
                    />
                  </div>
                </aside>
              ))}
            <div
              className={cn(
                "min-w-0",
                !isShortcut && !stepperCollapsed ? "flex-1" : "mx-auto w-full",
                currentStep === 5 ? "" : "max-w-4xl",
              )}
            >
              {/* Step Content */}
              <Card className={cn("mb-8 border-border bg-card", currentStep === 5 ? "p-4" : "p-8")}>
                {renderStep()}
              </Card>

              {/* Navigation */}
              <div className="flex justify-between">
                {!isShortcut && (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (currentStep === 0) {
                          navigate("/dashboard");
                        } else {
                          // Save before going back. Scope to the current step so
                          // we only write the section that step actually owns.
                          try {
                            await handleSave("draft", false, stepScope(currentStep));
                          } catch (error) {
                            console.error(error);
                            // Don't block navigation on error
                          }
                          setCurrentStep(currentStep - 1);
                        }
                      }}
                      disabled={isSaving}
                    >
                      <ChevronLeft className="mr-2 h-4 w-4" />
                      Previous
                    </Button>
                    {effectiveId && currentStep === 0 && (
                      <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  {isShortcut ? (
                    <Button
                      onClick={async () => {
                        // Mirror the Next button's isAdvancing guard so the
                        // button stays disabled + spinner-on through the
                        // entire save → invalidate → navigate sequence.
                        // Without this, `isSaving` flips back to false the
                        // moment handleSave returns, and the button is
                        // briefly clickable + flicker-free between save end
                        // and route change.
                        if (isAdvancing) return;
                        setIsAdvancing(true);

                        // Validate current step
                        const validation = await validateStep(currentStep);
                        if (!validation.valid) {
                          // Check if it's a warning (empty lineup or unassigned artists)
                          if ((validation as ValidationResult).warning && currentStep === 2) {
                            setLineupWarningMessage(validation.message || "");
                            setEmptyLineupDialogOpen(true);
                            setIsAdvancing(false);
                            return;
                          }
                          if ((validation as ValidationResult).warning && currentStep === 5) {
                            setUnassignedArtistsMessage(validation.message || "");
                            setUnassignedArtistsDialogOpen(true);
                            setIsAdvancing(false);
                            return;
                          }

                          toast({
                            title: "Validation Error",
                            description: validation.message,
                            variant: "destructive",
                          });
                          setIsAdvancing(false);
                          return;
                        }

                        try {
                          // Track briefing changes for Step 6 (General briefing)
                          const shouldTrackChanges = currentStep === 6;
                          // Save & Return is a quick-edit flow — scope to the
                          // current step so the partial save stays partial.
                          await handleSave(
                            existingBriefing?.status === "published" ? "published" : "draft",
                            shouldTrackChanges,
                            stepScope(currentStep),
                          );
                          toast({
                            title: "Changes saved",
                            description: "Your changes have been saved successfully.",
                          });
                          // A quick edit always returns to the overview
                          // dashboard of the production being edited — never a
                          // sub-page or a further wizard step.
                          if (effectiveId) {
                            navigate(productionOverviewPath(effectiveId));
                          }
                        } catch (error) {
                          console.error(error);
                          setIsAdvancing(false);
                        }
                        // intentional: leave isAdvancing true on success —
                        // navigate() unmounts the component, no need to reset
                      }}
                      disabled={isSaving || isAdvancing}
                    >
                      {isSaving || isAdvancing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save & Return
                    </Button>
                  ) : (
                    <>
                      {effectiveId && existingBriefing?.status !== "published" && (
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!effectiveId) {
                              toast({
                                title: "Error",
                                description: "No production ID found. Please try again.",
                                variant: "destructive",
                              });
                              return;
                            }

                            try {
                              await handleSave(
                                existingBriefing?.status === "published" ? "published" : "draft",
                              );
                            } catch (error) {
                              // handleSave already surfaced a toast and re-threw;
                              // swallow here so the rejection doesn't escape as an
                              // unhandled rejection (the original Sentry crash).
                              console.error(error);
                            }
                          }}
                          disabled={isSaving}
                          data-guide="step-3-tip-2"
                        >
                          {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          Save draft
                        </Button>
                      )}
                      <Button
                        onClick={async () => {
                          if (isAdvancing) return; // ref-style guard against double-click
                          setIsAdvancing(true);
                          try {
                            // Validate current step
                            const validation = await validateStep(currentStep);
                            if (!validation.valid) {
                              // Check if it's a warning (empty lineup or unassigned artists)
                              if ((validation as ValidationResult).warning && currentStep === 2) {
                                setEmptyLineupDialogOpen(true);
                                return;
                              }
                              if ((validation as ValidationResult).warning && currentStep === 5) {
                                setUnassignedArtistsMessage(validation.message || "");
                                setUnassignedArtistsDialogOpen(true);
                                return;
                              }

                              toast({
                                title: "Validation Error",
                                description: validation.message,
                                variant: "destructive",
                              });
                              return;
                            }

                            // Check if current step is the last step (Review & Publish is now step 8)
                            if (currentStep < STEPS.length - 1) {
                              // Not the last step - save and go to next step. Scope
                              // to the current step so we only write what changed.
                              try {
                                await handleSave("draft", false, stepScope(currentStep));
                              } catch (error) {
                                console.error(error);
                                // Don't block navigation on error
                              }
                              setCurrentStep(currentStep + 1);
                              return;
                            }

                            // Check production limit for NEW productions on Step 0 (PRO tier only)
                            if (currentStep === 0 && !effectiveId) {
                              try {
                                if (session) {
                                  const { data: limitCheck, error } =
                                    await supabase.functions.invoke("check-production-limit");

                                  if (error) {
                                    console.error(error);
                                  } else if (
                                    limitCheck &&
                                    !limitCheck.can_create &&
                                    limitCheck.limit_reached
                                  ) {
                                    toast({
                                      title: "Production limit reached",
                                      description: `Your PRO plan allows ${PRO_PRODUCTIONS_PER_YEAR} productions per calendar year (${limitCheck.productions_this_year}/${PRO_PRODUCTIONS_PER_YEAR} used). Upgrade to STUDIO for unlimited productions.`,
                                      variant: "destructive",
                                      action: (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => navigate("/upgrade")}
                                        >
                                          Upgrade to STUDIO
                                        </Button>
                                      ),
                                      duration: 8000,
                                    });
                                    return;
                                  }
                                }
                              } catch (error) {
                                console.error(error);
                              }
                            }

                            // Check if we need to show AI disclaimer
                            if (currentStep === 2 && usedAiExtraction) {
                              setAiDisclaimerOpen(true);
                              return;
                            }

                            if (currentStep < STEPS.length - 1) {
                              try {
                                // Save and move to next step (scope = current step)
                                await handleSave(
                                  existingBriefing?.status === "published" ? "published" : "draft",
                                  false,
                                  stepScope(currentStep),
                                );
                                setCurrentStep(currentStep + 1);
                              } catch (error) {
                                console.error(error);
                                // Error toast will be shown by handleSave
                              }
                            } else {
                              // For published productions, just save changes
                              if (existingBriefing?.status === "published") {
                                try {
                                  await handleSave("published");
                                  toast({
                                    title: "Changes saved",
                                    description: "Your changes have been saved successfully.",
                                  });
                                  navigate("/dashboard");
                                } catch (error) {
                                  console.error(error);
                                }
                              } else {
                                // Show publish confirmation for draft productions
                                setPublishConfirmOpen(true);
                              }
                            }
                          } finally {
                            setIsAdvancing(false);
                          }
                        }}
                        disabled={isSaving || isAdvancing}
                        data-guide="step-8-tip-2"
                      >
                        {isSaving || isAdvancing ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        {currentStep === STEPS.length - 1
                          ? existingBriefing?.status === "published"
                            ? "Save changes"
                            : "Publish and invite crew"
                          : "Next"}
                        {currentStep < STEPS.length - 1 && (
                          <ChevronRight className="ml-2 h-4 w-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Lineup-arrow swap confirmation. Surfaces the predicted
              crew-assignment impact (relabel / time-move / unassign)
              BEFORE the swap takes effect, so the producer can cancel
              when the auto-rebind doesn't match their intent. Skipped
              entirely when the swap touches no assignments. */}
              <AlertDialog
                open={pendingMoveSwap !== null}
                onOpenChange={(open) => {
                  if (!open) setPendingMoveSwap(null);
                }}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Swap will update crew assignments
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3">
                      <p>
                        Swapping <strong>{pendingMoveSwap?.fromArtist || "—"}</strong> and{" "}
                        <strong>{pendingMoveSwap?.toArtist || "—"}</strong> at{" "}
                        <strong>{pendingMoveSwap?.stageName || "this stage"}</strong> will
                        automatically:
                      </p>
                      <ul className="ml-4 list-disc space-y-1 text-sm">
                        {pendingMoveSwap && pendingMoveSwap.relabeled > 0 && (
                          <li>
                            Relabel <strong>{pendingMoveSwap.relabeled}</strong> crew assignment
                            {pendingMoveSwap.relabeled === 1 ? "" : "s"} to the new artist at the
                            same time slot
                          </li>
                        )}
                        {pendingMoveSwap && pendingMoveSwap.timeMoved > 0 && (
                          <li>
                            Move <strong>{pendingMoveSwap.timeMoved}</strong> crew assignment
                            {pendingMoveSwap.timeMoved === 1 ? "" : "s"} to follow their artist's
                            new time
                          </li>
                        )}
                        {pendingMoveSwap && pendingMoveSwap.deactivated > 0 && (
                          <li className="text-destructive">
                            Unassign <strong>{pendingMoveSwap.deactivated}</strong> crew assignment
                            {pendingMoveSwap.deactivated === 1 ? "" : "s"} (filming ran beyond the
                            new slot)
                          </li>
                        )}
                      </ul>
                      <p className="text-sm">
                        Changes apply when you save the briefing. You can still adjust them in the
                        crew schedule step before saving.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        pendingMoveSwap?.apply();
                        setPendingMoveSwap(null);
                      }}
                    >
                      Swap and update assignments
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Delete Confirmation Dialog */}
              <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      Delete production
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-4">
                      <p>
                        This action cannot be undone. This will permanently delete the production
                        and all associated data including lineup, crew, and schedules.
                      </p>
                      <div>
                        <Label htmlFor="delete-confirm">
                          Type <strong>DELETE</strong> to confirm
                        </Label>
                        <Input
                          id="delete-confirm"
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="DELETE"
                          className="mt-2"
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={deleteConfirmText !== "DELETE"}
                      onClick={async () => {
                        if (deleteConfirmText === "DELETE" && effectiveId) {
                          try {
                            // Delete briefing - CASCADE will handle all related data
                            const { error } = await supabase
                              .from("briefings")
                              .delete()
                              .eq("id", effectiveId);

                            if (error) throw error;

                            clearProductionState();

                            toast({
                              title: "Production deleted",
                              description: "The production has been permanently deleted.",
                            });
                            navigate("/dashboard");
                          } catch {
                            toast({
                              title: "Error",
                              description: "Failed to delete production.",
                              variant: "destructive",
                            });
                          }
                        }
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete production
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* AI Disclaimer Dialog */}
              <AlertDialog open={aiDisclaimerOpen} onOpenChange={setAiDisclaimerOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Verify AI-Generated Timing
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Please double-check all timing information as AI may make mistakes. Verify all
                      set times are accurate before publishing.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setAiDisclaimerOpen(false)}>
                      Go Back
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setAiDisclaimerOpen(false);
                        setUsedAiExtraction(false);
                        // Continue to next step
                        try {
                          await handleSave("draft", false, stepScope(currentStep));
                          setCurrentStep(currentStep + 1);
                        } catch (error) {
                          console.error(error);
                        }
                      }}
                    >
                      Confirm
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Empty Lineup Warning Dialog */}
              <AlertDialog open={emptyLineupDialogOpen} onOpenChange={setEmptyLineupDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Timetable Warning
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {lineupWarningMessage ||
                        "No lineup has been entered. Without a lineup, no crew schedule can be generated with AI."}
                      <br />
                      <br />
                      Are you sure you want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setEmptyLineupDialogOpen(false)}>
                      Go Back
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => continueFromWarning(() => setEmptyLineupDialogOpen(false))}
                    >
                      Continue anyway
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Unassigned Artists Warning Dialog */}
              <AlertDialog
                open={unassignedArtistsDialogOpen}
                onOpenChange={setUnassignedArtistsDialogOpen}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Unassigned Artists
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {unassignedArtistsMessage}
                      <br />
                      <br />
                      Do you really want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setUnassignedArtistsDialogOpen(false)}>
                      Go Back
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() =>
                        continueFromWarning(() => setUnassignedArtistsDialogOpen(false))
                      }
                    >
                      Continue anyway
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Publish Confirmation Dialog */}
              <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Publish production
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Once this production is published, you'll be redirected to the Manage Crew
                      page where you can invite all crew members.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setPublishConfirmOpen(false)}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setPublishConfirmOpen(false);
                        try {
                          // Re-check production limit before publishing (PRO tier)
                          if (session) {
                            const { data: limitCheck } =
                              await supabase.functions.invoke("check-production-limit");
                            if (limitCheck && !limitCheck.can_create && limitCheck.limit_reached) {
                              toast({
                                title: "Production limit reached",
                                description: `Your PRO plan allows ${PRO_PRODUCTIONS_PER_YEAR} productions per calendar year (${limitCheck.productions_this_year}/${PRO_PRODUCTIONS_PER_YEAR} used). Upgrade to STUDIO for unlimited productions.`,
                                variant: "destructive",
                                action: (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate("/upgrade")}
                                  >
                                    Upgrade to STUDIO
                                  </Button>
                                ),
                                duration: 8000,
                              });
                              return;
                            }
                          }
                          await handleSave("published");
                        } catch (error) {
                          console.error(error);
                        }
                      }}
                    >
                      Confirm publish
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Date Change Warning Dialog */}
              <DateChangeWarningDialog
                open={dateChangeDialogOpen}
                onOpenChange={setDateChangeDialogOpen}
                onClose={() => setDateChangeDialogOpen(false)}
              />
            </div>
          </div>

          {/* Briefing Onboarding Guide */}
          {showBriefingGuide && briefingGuideSteps.length > 0 && (
            <OnboardingGuide
              steps={briefingGuideSteps}
              onComplete={handleBriefingGuideComplete}
              onSkip={handleBriefingGuideComplete}
            />
          )}
        </div>
      </main>
    </div>
  );
};
export default CreateEditBriefing;
